// ===== INCOME & EXPENSES MODULE =====
// Core imports
import { S, save, stampItem, tombstoneId, softDelete } from '../core/state.js';
import { renderStatements } from './statements.js';
import { f, fU, fT, esc, uid, now, toUSD, toUSDStrict, usdToTRY } from '../core/utils.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm } from '../ui/dialog.js';
import { isPlanFunding } from '../core/capital.js';
import { _safeChart, _safeDestroy, getChartTheme } from '../ui/charts.js';
import { lg } from '../modules/history.js';
import { renderBudget } from '../modules/budget.js';
import { resetWhatIf, populateWIExpenses } from '../modules/whatif.js';
import { markDirty } from '../app/render-loop.js';
import { runDebtPayoff } from '../modules/debt.js';

let editExpDefId=null;
let cfActiveMo=null; // month key open in modal

/**
 * KARMA — THE ONE DEFINITION OF AN EXPENSE THIS HOUSEHOLD DOES NOT PAY.
 *
 * The mortgage is in the book because the owner wants to TRACK it; his wife pays it. It is
 * recorded with paidBy:'mixed' ("Karma" in the UI) and must therefore never reduce his
 * surplus, his burn, his disposable income or his forward capacity — it is a fact about the
 * household, not a claim on his salary.
 *
 * That rule was already written. Eighteen times, by hand, and eleven of the copies had it:
 *
 *     mo.items.reduce((s,it) => s + (it.confirmed && it.amount && it.paidBy!=='mixed' ? …))
 *
 * The seven that did not are the ones nobody was looking at, and they are not small:
 *   _cfNeo's 12-month bars, and through medExp both the 12-month forward capacity AND the
 *     "avg monthly disposable" tile — ₺68,000/month of somebody else's mortgage;
 *   renderProjection's burn ring, both halves, so it read ₺10,982 / ₺115,613;
 *   the all-months "Total Expenses" / "Total Surplus" summary;
 *   dashboard's budget alert, which could fire "Budget exceeded" on a bill he never pays;
 *   and ai-insights, which handed the inflated figure to the model as fact — a fabricated
 *     burden reasoned over and returned as advice.
 *
 * So the same page showed SURPLUS +₺61,337 in its hero and a 12-month capacity of
 * −₺1,202,652 underneath it, computed on two different definitions of the word "expense".
 * This is the fourth time this codebase has been bitten by a rule with N copies. One
 * definition, and a test that fails if a twelfth copy appears. (audit 2026-08-04)
 */
const KARMA = 'mixed';
/** True when somebody outside this household pays the item. */
function isKarma(it) { return !!it && it.paidBy === KARMA; }

/**
 * THE ANCHOR. Everything cash-related hangs off one date.
 *
 * `S.cash.TRY` is set to the banks' own figure by Reconcile TRY. Trades, FX conversions and
 * portfolio buys move it from there; salary and household bills never have, which is how it
 * came to sit ₺102,415 below the banks on 2026-08-08 with nothing able to show it.
 *
 * Making those two mutate it was tried on 2026-08-08 and reverted the same day — see the
 * header of tests/core/cash-derived.test.js for the five ways it failed. They are DERIVED
 * instead, in projectedCashTRY, and only for rows dated after the reading: a bill paid on the
 * 3rd is already inside the balance he read on the 8th, and counting it again invents a second
 * payment. On or before the anchor: already counted, hands off.
 */
export function cashAnchorDay() {
  const at = S.cashRecon && S.cashRecon.TRY ? S.cashRecon.TRY.at : null;
  if (!at) return null;
  // LOCAL calendar day, not the UTC slice. now() is UTC ISO, so a reconciliation at 01:00 in
  // Istanbul stamps the PREVIOUS day — which would have made the 1st-of-month salary, already
  // inside the reading he had just typed, test as money that arrived after it. A three-hour
  // window every night, on exactly the comparison the anchor exists to get right.
  return _localDay(new Date(at));
}
function _localDay(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function todayLocal() { return _localDay(new Date()); }
function _rowDate(monthKey, day) {
  const [y, m] = String(monthKey).split('-');
  return `${y}-${m}-${String(day || 1).padStart(2, '0')}`;
}
/**
 * True when a row dated (monthKey, day) is money that has ALREADY moved since the last bank
 * reading. Two bounds, and the upper one is not optional: the month selector offers the next
 * three months, so without it next month's salary counts as cash in hand today.
 */
export function afterAnchor(monthKey, day) {
  const a = cashAnchorDay();
  if (!a) return false;
  const d = _rowDate(monthKey, day);
  return d > a && d <= todayLocal();
}
/**
 * Has a confirmed row's money left the account since the last bank reading?
 *
 * THE DUE DATE IS NOT A PAYMENT DATE, and for a whole day the difference is unanswerable.
 * Comparing due dates against the anchor produced two opposite failures on the same data:
 *
 *   - ticking the 24th's instalment on the 8th and NOT counting it made `free` rise by what
 *     he had just spent (fixed 2026-08-08 by ignoring the "has it happened yet" bound);
 *   - ticking the 11th's card bill on the 10th and then reading the banks that afternoon
 *     counted it AGAIN, on top of a balance that already contained it (found 2026-08-10).
 *
 * Both are the same missing fact: WHEN he paid. `confirmedAt` is a full timestamp stamped by
 * cfToggle from 2026-08-10, and where it exists the question is answered exactly, to the
 * instant, in both directions.
 *
 * Rows confirmed BEFORE that stamp existed keep the old rule unchanged — the tick is the
 * evidence the money moved, the due date is all there is, and inventing a payment time for
 * them would be inference dressed as fact. They stamp themselves the next time he touches one.
 */
function _paidSinceAnchor(monthKey, it) {
  const rec = (S.cashRecon && S.cashRecon.TRY) || null;
  const atMs = rec && rec.at ? Date.parse(rec.at) : NaN;
  const stampMs = it && it.confirmedAt ? Date.parse(it.confirmedAt) : NaN;
  if (isFinite(atMs) && isFinite(stampMs)) return stampMs > atMs;
  const a = cashAnchorDay();
  return !!a && _rowDate(monthKey, it && it.day) > a;
}
function _cfItems(mo) { return (mo && Array.isArray(mo.items)) ? mo.items : []; }
/**
 * Income entries for a month, including a legacy month that still holds the scalar `mo.income`
 * and has no `incomes[]`. projectedCashTRY reads S.cashflow raw — it must not depend on
 * cfGetMonth having seeded the array, or merely OPENING the month modal would move a money
 * figure by a full salary.
 */
function _monthIncomes(mo) {
  if (!mo) return [];
  if (Array.isArray(mo.incomes) && mo.incomes.length) return mo.incomes;
  const legacy = parseFloat(mo.income) || 0;
  return legacy > 0 ? [{ id: '_legacy', day: 1, type: 'Salary', amount: legacy }] : [];
}
function _cfSum(mo, keep, confirmedOnly) {
  return _cfItems(mo).reduce((s, it) =>
    s + ((keep(it) && it.amount && (!confirmedOnly || it.confirmed)) ? it.amount : 0), 0);
}
/** What this household actually pays out in `mo`. Karma is never in it. */
function monthOutflowTRY(mo, confirmedOnly = false) { return _cfSum(mo, it => !isKarma(it), confirmedOnly); }

/**
 * What the TRY balance is heading for, given what is still due this month.
 *
 * DERIVED, never stored. The whole reason this function exists rather than a running balance
 * is the 2026-08-08 finding: `S.cash.TRY` had drifted ₺102,415 from the banks and nothing
 * could tell, because a maintained figure that nobody re-verifies has no error signal. So the
 * stored number is only ever "what the banks said at `cashRecon.TRY.at`", and everything
 * forward-looking is computed from the cashflow month each time it is read.
 *
 * Items dated on or before the anchor are already inside the balance the owner read off the
 * bank, confirmed or not, and must not be subtracted twice. Karma is never subtracted at all
 * (see the KARMA block at the top of this file).
 *
 * Returns nulls rather than a guess when there is no anchor — an unverified balance has no
 * projection, and a projection drawn from an unverified balance is exactly the class of
 * plausible-looking figure this audit was about.
 */
/**
 * How much of the TRY balance is actually his to deploy.
 *
 * ONE function, because three surfaces needed this answer at once and the alternative is three
 * hand-written copies of a rule — the pattern this codebase has been bitten by six times. The
 * Cash hero calls ₺336,800 "sitting in TRY earning nothing"; the panel four rows below it says
 * ₺296,030 of that is owed or committed. Every AI prompt was handed the first number with an
 * instruction to hunt for cash drag. The liability affordability preview and a TRY cash goal
 * read it raw too.
 *
 * `free` is what remains after this month's unpaid bills and the un-transferred part of the
 * plan. It can go negative — that is a real state (August 2026 closes ₺40,770 clear only
 * because a trade exit funded it) and it must not be clamped, or the one month that needs the
 * warning is the month that does not get it.
 *
 * `ok:false` means nothing has been reconciled or the month has no record: `committed` is then
 * unknown, NOT zero. Callers must render that as unknown rather than as "all of it is free".
 */
export function tryCashPosture(monthKey) {
  const p = projectedCashTRY(monthKey);
  if (!p.ok) return { ok: false, why: p.why, current: p.current, committed: null, free: null, remaining: null, commitment: null, month: p.month };
  const committed = p.remaining + p.commitment;
  return { ok: true, why: null, current: p.current, committed, free: p.current - committed, remaining: p.remaining, commitment: p.commitment, month: p.month };
}

/**
 * The TRY posture, shaped for an LLM prompt. ONE copy: the first attempt shipped this block
 * byte-identically into ai-insights.js, ai-chat.js and ai-copilot.js — three hand-written
 * copies of a rule, in the same change whose own docstring names that as the pattern this
 * codebase has been bitten by six times. Fixing a sentence would have meant editing three
 * files with nothing failing if one were missed.
 */
export function tryPostureForPrompt() {
  try {
    const p = tryCashPosture();
    if (p.ok) {
      return {
        tryCommittedTRY: +p.committed.toFixed(2),
        tryFreeTRY: +p.free.toFixed(2),
        note: "tryCommittedTRY is this month's unpaid bills plus the un-transferred portfolio commitment. Only tryFreeTRY is deployable; never advise deploying committed cash.",
      };
    }
    return {
      tryCommittedTRY: null, tryFreeTRY: null,
      note: p.why === 'no-month'
        ? 'The current month has no cash-flow record yet, so the committed portion of the TRY balance is UNKNOWN — do not treat the TRY balance as deployable.'
        : 'TRY cash has not been reconciled against the banks, so the committed portion is UNKNOWN — do not treat the TRY balance as deployable.',
    };
  } catch (_e) {
    return { tryCommittedTRY: null, tryFreeTRY: null, note: 'TRY commitment unavailable — do not treat the TRY balance as deployable.' };
  }
}

/**
 * Is this row's money going to leave the TRY CURRENT ACCOUNT?
 *
 * Karma is somebody else's. 'savings' means he named a source: 'cash' is already debited by
 * cfToggle and must not be counted twice, 'reserve' comes out of the reserve fund, 'td' out of
 * a deposit the file says he settles by hand. 'other' is by definition not this account.
 * That leaves 'income' and the unset default, which is what an ordinary bill is.
 */
function _fromTryCash(it) {
  if (!it || isKarma(it) || !(it.amount > 0)) return false;
  return !it.paidBy || it.paidBy === 'income';
}

/**
 * What the TRY current account holds now, and where the month ends.
 *
 * DERIVED, never stored — see the note on `cashAnchorDay`. `S.cash.TRY` carries trades,
 * transfers and portfolio buys and nothing else; salary and household bills are added and
 * subtracted here, on read.
 *
 * The window is (last bank reading, today]. It is NOT the month: the first version summed the
 * corrections inside `monthKey` while the window spanned whatever time had passed, so on the
 * 1st of every month the figure jumped by the whole of the previous month's spend — ₺350,407
 * overnight, on the owner's own numbers, with no transaction behind it. Every month that
 * overlaps the window is summed.
 */
export function projectedCashTRY(monthKey) {
  const rec = (S.cashRecon && S.cashRecon.TRY) || null;
  const at = rec ? rec.at : null;
  const ledger = Number(S.cash && S.cash.TRY) || 0;
  const verified = rec && isFinite(Number(rec.total)) ? Number(rec.total) : null;
  const base = { ok: false, why: 'unreconciled', at: at || null, month: null, verified: null, ledger, current: ledger, salaryIn: 0, paidOut: 0, remaining: null, commitment: null, transferred: null, projected: null, items: [] };
  if (!at) return base;

  const k = monthKey || todayLocal().slice(0, 7);

  // Corrections across EVERY month the window touches, using the one anchor predicate rather
  // than a second inline copy of it.
  let salaryIn = 0, paidOut = 0;
  Object.entries(S.cashflow || {}).forEach(([mk, m]) => {
    if (!m) return;
    _monthIncomes(m).forEach(e => {
      if (afterAnchor(mk, e.day || 1)) salaryIn += parseFloat(e.amount) || 0;
    });
    _cfItems(m).forEach(it => {
      // `confirmed` IS the evidence the money moved, so the upper bound does not apply here —
      // it does for income, where the date is the only evidence. Paying the 24th's instalment
      // on the 8th used to drop it from `remaining` without adding it to `paidOut`, and `free`
      // ROSE by what he had just spent: tick all five August bills early and it read ₺168,395
      // deployable against a true ₺40,770.
      if (it && it.confirmed && _fromTryCash(it) && _paidSinceAnchor(mk, it)) paidOut += it.amount;
    });
  });
  const current = ledger + salaryIn - paidOut;

  const mo = (S.cashflow || {})[k];
  // Not the same sentence as "never reconciled". Telling him to reconcile on the 1st of a month,
  // three weeks after he did, is the contradiction this file already forbids one screen over.
  if (!mo) return { ...base, why: 'no-month', at, month: k, verified, salaryIn, paidOut, current };

  // Everything still owed this month out of THIS account, paid or not yet due. An unpaid bill
  // dated before the reading is still unpaid — the anchor governs what has moved, not what is owed.
  const due = _cfItems(mo).filter(it => it && !it.confirmed && _fromTryCash(it));
  const remaining = due.reduce((a, it) => a + it.amount, 0);

  // The plan transfer, net of what has already gone out. Only the TRY→USD legs of an FX
  // conversion: an earlier version counted `portfolio_buy` and read ₺150,478.90 "transferred"
  // on the owner's live data because it swept up ₺91,215.91 of BIST purchases. A TRY→EUR
  // conversion is not this transfer either, and a conversion back has to net off — otherwise
  // sending ₺100,000 and returning ₺60,000 reads as ₺100,000 sent.
  const transferred = Math.max(0, (S.cashTxs || []).reduce((a, tx) => {
    if (!tx || tx.currency !== 'TRY' || String(tx.date || '').slice(0, 7) !== k) return a;
    if (tx.type === 'fx_convert') {
      // ⚠ WHERE THE DOLLARS WENT, not just that lira became dollars. Without this the Garanti
      // BBVA Kripto purchase of 2026-09-11 — a real TRY→USD conversion on a dollar-denominated
      // venue — would have read as ₺371,747 wired to IBKR for the monthly plan. The assumption
      // that a conversion IS the plan wire held only because every conversion so far had been.
      if (!isPlanFunding(tx)) return a;
      if (tx.amount < 0) return tx.fxTo === 'USD' ? a + Math.abs(tx.amount) : a;
      return tx.fxFrom === 'USD' ? a - tx.amount : a;
    }
    // The Cash Transactions row also offers a plain Withdraw, and its controlled vocabulary has
    // exactly two reasons that mean "this left for investing". Wiring the ₺168,405 that way and
    // not netting it subtracted the same money twice — once through the ledger, once through the
    // un-netted commitment — and the hero read "over-committed by ₺127,635" with nothing wrong.
    if (tx.type === 'withdraw' && tx.amount < 0 && (tx.reason === 'Investment' || tx.reason === 'Transfer Out')) {
      return a + Math.abs(tx.amount);
    }
    return a;
  }, 0));
  // NEW MONEY only, through the one helper that defines it. A second reading of the same
  // quantity is how eleven copies of "cash in USD" happened. (CONTRACT-004)
  const commitment = Math.max(0, monthDeployment(mo).newMoney - transferred);

  return {
    ok: true, at, month: k, verified, ledger, current, salaryIn, paidOut,
    remaining, commitment, transferred,
    projected: current - remaining - commitment,
    items: due.map(it => ({ day: it.day, name: it.name, amount: it.amount, confirmed: !!it.confirmed })),
  };
}
/**
 * Close the legacy gap at the one moment it can be closed with a FACT rather than a guess.
 *
 * Rows confirmed before `confirmedAt` existed carry only a due date, and a due date after the
 * reconciliation day keeps testing as "still to come" — so the next reading, which already
 * contains that payment, subtracts it a second time. On the live 2026-08-10 data that was
 * ₺71,927 (a card bill dated the 11th, paid on the 10th), and with the headline figures now
 * routed through cashTRYNow() it would have propagated to every one of them.
 *
 * A migration cannot fix this: it would have to invent a payment time. But at the instant he
 * types the bank balances, one thing is true by definition — everything already ticked is
 * inside the figure he just typed. So stamp exactly that, and only where nothing is known yet.
 *
 * Called by reconcileCashTRY AFTER the new anchor is written. (audit 2026-08-10, round 2)
 */
export function stampConfirmedBeforeReading(atISO) {
  const at = String(atISO || '');
  if (!at) return 0;
  let n = 0;
  Object.values(S.cashflow || {}).forEach(m => {
    _cfItems(m).forEach(it => {
      if (it && it.confirmed && !it.confirmedAt) { it.confirmedAt = at; n++; }
    });
  });
  return n;
}

/**
 * THE TRY CURRENT ACCOUNT AS OF NOW, not as of the last time he read the banks.
 *
 * `S.cash.TRY` is the reconciled reading plus whatever has crossed the LEDGER since — trades,
 * transfers, portfolio buys, a tax refund. Household bills are not in the ledger by design
 * (booking them there is the "credited" model that produced five CRITICALs on 2026-08-08), so
 * between two reconciliations the stored figure drifts UPWARD by everything he has paid.
 *
 * On 2026-08-10 that gap was ₺76,101: two card payments confirmed the same day. The Cash
 * page's TRY panel had it right at ₺354,368.31 while TOTAL LIQUID, Total Wealth, Total Cash,
 * the Goals progress and every AI prompt read ₺430,469.31 — because each of them had its own
 * hand-written copy of "cash in USD" summing S.cash directly. SIX copies of one rule.
 *
 * Owner's decision, 2026-08-10: the headline figure is what is in the account now.
 * Double-counting is impossible by construction — the moment he reconciles, the anchor moves
 * to today and those same items stop qualifying.
 */
export function cashTRYNow() {
  try {
    const p = projectedCashTRY();
    return isFinite(p.current) ? p.current : (Number(S.cash && S.cash.TRY) || 0);
  } catch (_e) {
    // Never a zero: a thrown projection must not read as an empty account.
    return Number(S.cash && S.cash.TRY) || 0;
  }
}

/**
 * Total cash in USD. THE canonical one — dashboard (twice), the Cash hero, Portfolio, Goals
 * and two AI prompts each had their own. USD and EUR are unmodelled by the cash-flow month, so
 * they pass through; only TRY has a household ledger behind it.
 */
export function cashTotalUSD() {
  return (Number(S.cash && S.cash.USD) || 0)
    + toUSD(cashTRYNow(), 'TRY')
    + toUSD(Number(S.cash && S.cash.EUR) || 0, 'EUR');
}

/** What somebody else pays in `mo`. Shown, never billed. */
function monthKarmaTRY(mo, confirmedOnly = false) { return _cfSum(mo, isKarma, confirmedOnly); }

/**
 * RECURRING INCOME — the other half of the same idea, on the income side.
 *
 * A bonus is money. It is not a salary. Anything that projects forward — the 12-month
 * capacity, the "avg monthly disposable" tile, the forward income table — must be built on
 * what repeats, or one good month becomes a permanent assumption.
 *
 * The rule existed in exactly one function, projectMonthlyIncome, and it was a NOTE REGEX:
 * an entry counted as a bonus if the owner had typed the word "bonus", "prim" or "ikramiye"
 * into a free-text field. The type dropdown offered only Salary and Other, so there was no
 * way to say it structurally. Write "Q3 ödemesi" instead and the projection quietly assumed
 * he earned a bonus every month, forever. Meanwhile _cfNeo's medInc — which feeds the two
 * figures on the Money page most likely to be acted on — did not strip anything at all.
 * Two definitions of "monthly income", one of them spelling-dependent.
 *
 * Now: the entry TYPE decides, the note regex stays only so months recorded before this
 * still classify correctly, and both consumers call the same function. Which method the
 * owner uses to record a bonus — a separate income row, or a One-Time Income entry — no
 * longer changes any projected figure. (audit 2026-08-04)
 */
const ONEOFF_TYPE = /^(bonus|commission|severance|tax refund|gift|insurance payout|sale proceeds)$/i;
const ONEOFF_NOTE = /bonus|prim|ikramiye/i;
/** True when the entry is expected again next month. */
function isRecurringIncome(e) {
  if (!e) return false;
  if (ONEOFF_TYPE.test(String(e.type || ''))) return false;
  if (ONEOFF_NOTE.test(String(e.note || ''))) return false;   // pre-2026-08-04 months
  return true;
}
/** The part of `mo`'s income that repeats. Legacy months with no entries fall back to the scalar. */
function recurringIncomeTRY(mo) {
  const entries = (mo && Array.isArray(mo.incomes)) ? mo.incomes : [];
  if (!entries.length) return (mo && mo.income) || 0;
  return entries.reduce((s, e) => s + (isRecurringIncome(e) ? (parseFloat(e.amount) || 0) : 0), 0);
}
/** The part that does not. Shown beside the total so the classification is visible. */
function oneOffIncomeTRY(mo) { return Math.max(0, ((mo && mo.income) || 0) - recurringIncomeTRY(mo)); }

/**
 * THE TWO QUANTITIES THAT WERE ONE FIELD UNTIL 2026-08-14 (CONTRACT-004).
 *
 * `portfolioComm` is NEW MONEY: what leaves this month's income for the portfolio. It is a
 * claim on income that has not been spent yet, which is why `commitment` subtracts it from
 * free TRY and why every surplus line subtracts it from disposable income.
 *
 * `redeployed` is capital recycled from a sale. `S.cash` was debited when the buy was booked,
 * so subtracting it a second time understates free TRY by exactly its own size — measured at
 * ₺113,836 across 2026-08-08..13, on a number that reaches the Money hero, the cash
 * projection, TRY IDLE SHARE, the liability payment preview and the LLM prompt.
 *
 * So `redeployed` enters NO subtraction anywhere. It exists so a rebalance month can say what
 * it actually deployed without the saying of it moving a balance. Every surface that wants
 * that sentence calls this function; nothing recomputes the pair by hand.
 */
export function monthDeployment(mo){
  const newMoney=Math.max(0,Number(mo&&mo.portfolioComm)||0);
  const redeployed=Math.max(0,Number(mo&&mo.redeployed)||0);
  return {newMoney,redeployed,total:newMoney+redeployed};
}

function cfGetMonth(k){
  if(!S.cashflow[k])S.cashflow[k]={income:0,incomes:[],portfolioComm:0,redeployed:0,items:[],closed:false};
  const mo=S.cashflow[k];
  if(!Array.isArray(mo.incomes))mo.incomes=[];
  // v49 stamps this on every month that already existed; a month created by an older build
  // on another device can still arrive through a merge, so the read path states it too.
  if(typeof mo.redeployed!=='number'||!isFinite(mo.redeployed))mo.redeployed=0;
  // Back-compat: if legacy scalar income exists but no entries, seed one
  if(mo.incomes.length===0 && (mo.income||0)>0){
    mo.incomes.push({id:uid(),day:1,amount:mo.income,type:'Salary',note:'Migrated'});
  }
  return mo;
}

// Recompute mo.income from entries (keeps back-compat with all downstream readers)
function cfRecalcIncome(mo){
  mo.income=(mo.incomes||[]).reduce((s,e)=>s+(parseFloat(e.amount)||0),0);
}

// ===== SALARY → CASH TRANSACTION BRIDGE =====
// Income cashTx records are AUDIT-ONLY: they appear in cash history for visibility and do NOT
// affect S.cash.TRY. Salary that has arrived since the last bank reading is added by
// projectedCashTRY on read instead — a credited version could not survive a reload, because
// backfillSalaryCashTxs resets `_audit` and rewrites the balance from the ledger on every boot.
// Note pattern: "Salary: {month}" or "Income: {type} ({month})"

function _incomeTxNote(entry, monthKey) {
  const type = (entry.type || 'Salary').toLowerCase();
  const note = entry.note ? ` — ${entry.note}` : '';
  if (type === 'salary') return `Salary: ${monthKey}${note}`;
  return `Income: ${entry.type} (${monthKey})${note}`;
}

// Find existing cashTx linked to an income entry by its incomeId tag
function _findIncomeCashTx(incomeId) {
  return (S.cashTxs || []).find(tx => tx._incomeId === incomeId);
}

// Sync a single income entry → cashTx (audit-only, no balance effect).
// Exported for tests. Writes the audit-trail row for one income entry and nothing else — it
// must never move a balance, and cash-derived.test.js pins that.
export function _syncOneIncomeTx(monthKey, entry) {
  if (!entry || !entry.id) return;
  const amount = parseFloat(entry.amount) || 0;
  const existing = _findIncomeCashTx(entry.id);

  if (amount <= 0) {
    // Zeroing an entry deletes its row. A second delete path next to _removeIncomeTx — worth
    // knowing about if anything is ever hung off income rows again.
    if (existing) { tombstoneId(existing.id); S.cashTxs = S.cashTxs.filter(tx => tx._incomeId !== entry.id); }
    return;
  }

  const note = _incomeTxNote(entry, monthKey);
  const [y, m] = monthKey.split('-');
  const day = String(entry.day || 1).padStart(2, '0');
  const dateStr = `${y}-${m}-${day}T12:00:00`;

  if (existing) {
    existing.amount = amount;
    existing.note = note;
    existing.date = dateStr;
    existing.reason = entry.type === 'Salary' ? 'Salary' : 'Other income';
    existing._audit = true;
    stampItem(existing);
  } else {
    S.cashTxs.unshift({
      id: uid(), date: dateStr, type: 'deposit',
      amount, currency: 'TRY',
      reason: entry.type === 'Salary' ? 'Salary' : 'Other income',
      note, _incomeId: entry.id, _audit: true, _updatedAt: now()
    });
  }
}

// Remove cashTx for a deleted income entry (no balance reversal needed — audit-only).
// Tombstone removed ids so a merge can't resurrect them and re-trigger the reconciler.
export function _removeIncomeTx(incomeId) {
  (S.cashTxs || []).forEach(tx => { if (tx._incomeId === incomeId) tombstoneId(tx.id); });
  S.cashTxs = S.cashTxs.filter(tx => tx._incomeId !== incomeId);
}

// Sync ALL income entries for a given month — called on saveCFMo / closeCFMonth
function _syncMonthIncomeTxs(monthKey) {
  const mo = S.cashflow[monthKey];
  if (!mo || !Array.isArray(mo.incomes)) return;
  mo.incomes.forEach(entry => _syncOneIncomeTx(monthKey, entry));
  // Clean up orphaned cashTxs for entries that were removed (tombstone so they don't resurrect)
  const entryIds = new Set(mo.incomes.map(e => e.id));
  S.cashTxs = S.cashTxs.filter(tx => {
    if (!tx._incomeId) return true;
    if (!(tx.note || '').includes(monthKey)) return true;
    const keep = entryIds.has(tx._incomeId);
    if (!keep) tombstoneId(tx.id);
    return keep;
  });
}

// Back-fill salary cashTxs for existing months that don't have them yet
// Called once on app init — safe to call multiple times (idempotent)
// Also runs a one-time migration to:
//   1) Reverse any cash.TRY inflation from the pre-audit-only version
//   2) Mark all salary cashTxs with _audit: true
function backfillSalaryCashTxs() {
  let created = 0;
  let fixed = false;

  // Step 1: Ensure ALL salary/income cashTxs have _audit flag
  (S.cashTxs || []).forEach(tx => {
    if (tx._incomeId && !tx._audit) {
      tx._audit = true;
      fixed = true;
    }
  });

  // Step 2: Consistency check — recalculate cash for currencies that have audit records.
  // The one-time migration approach was fragile: _audit flags and _salaryAuditMigrated
  // could persist to cloud while the cash reversal was overwritten by a concurrent cloudLoad.
  // Instead, we ALWAYS verify cash matches the sum of non-audit cashTxs.
  const auditByCur = {};
  const realNetByCur = {};
  (S.cashTxs || []).forEach(tx => {
    const cur = tx.currency || 'TRY';
    if (tx._audit) {
      auditByCur[cur] = (auditByCur[cur] || 0) + Math.abs(tx.amount || 0);
    } else {
      realNetByCur[cur] = (realNetByCur[cur] || 0) + (tx.amount || 0);
    }
  });

  // Only recalculate currencies that HAVE audit records (salary/income txs)
  // This avoids touching currencies where cashTx log might be incomplete
  //
  // ⚠⚠ THIS IS THE SECOND UNATTENDED WRITER OF `S.cash` ON THE BOOT PATH, and it was found by
  // sweeping for the class after MOVE-005 rather than by anything failing. `reconcile.js`'s
  // `cash_mismatch` computes the SAME quantity — the balance against the sum of the ledger — and
  // it **refuses to auto-fix when `S._truncated.cashTxs` is set**, because truncation removes old
  // rows and makes the sum a lie. That guard did not exist here. The cap is 2,000 rows and he is
  // at 120, so it has never fired; a book that grows past the cap would have had its balance
  // silently rewritten DOWNWARD to the surviving rows, on every load, with nothing but a history
  // line to show for it.
  //
  // ⚠ AND THE SECOND TRIGGER IS THE ONE TODAY NEARLY PULLED. Any input that moves the stored
  // balance more than ₺1 from the non-audit ledger sum arms this. The reconcile pump happened to
  // add cash AND a row, so the sum tracked and this stayed quiet — had it added one without the
  // other, two unattended boot writers would have been overwriting his balance in turn.
  //
  // Measured on `backup/zagx-2026-08-16.json`: 120 rows, 5 `_audit`, TRY drift ₺0.0023. Armed,
  // not firing.
  const _trunc = S._truncated && S._truncated.cashTxs;
  Object.keys(auditByCur).forEach(cur => {
    const expected = realNetByCur[cur] || 0;
    const actual = S.cash[cur] || 0;
    const drift = Math.abs(actual - expected);
    if (drift <= 1) return;
    if (_trunc) {
      // The sum is not the truth here, so the balance is left alone and the disagreement is said
      // out loud rather than resolved by guessing.
      console.warn(`[ZAGX] cash ${cur} drift ${Math.round(drift).toLocaleString()} NOT auto-corrected — the ledger was trimmed (${_trunc.dropped} row(s)), so its sum is not the balance`);
      lg('cashflow', '—', `Cash ${cur} drift ${Math.round(drift).toLocaleString()} left alone — ledger truncated`);
      toast(`Cash ${cur} disagrees with a trimmed ledger — review in Settings`, 'a-y');
      return;
    }
    // ⚠ SAY IT OUT LOUD. This rewrites a balance he can see, on a boot he did not ask for. It
    // used to leave a history line and nothing else; the reconcile pump ran for two hours under
    // a GREEN toast, and silence is worse than that.
    console.warn(`[ZAGX] cash ${cur} rewritten from the ledger: ${Math.round(actual).toLocaleString()} → ${Math.round(expected).toLocaleString()} (drift ${Math.round(drift).toLocaleString()})`);
    lg('cashflow', '—', `Cash ${cur} drift: ${Math.round(actual).toLocaleString()} → ${Math.round(expected).toLocaleString()} (audit inflation: ${Math.round(auditByCur[cur]).toLocaleString()})`);
    toast(`Cash ${cur} corrected to the ledger (${Math.round(drift).toLocaleString()} drift)`, 'a-y');
    S.cash[cur] = expected;
    fixed = true;
  });

  // Step 3: Back-fill missing entries
  Object.keys(S.cashflow || {}).forEach(monthKey => {
    const mo = S.cashflow[monthKey];
    if (!mo || !Array.isArray(mo.incomes)) return;
    mo.incomes.forEach(entry => {
      if (!entry.id || (parseFloat(entry.amount) || 0) <= 0) return;
      if (_findIncomeCashTx(entry.id)) return; // already synced
      _syncOneIncomeTx(monthKey, entry);
      created++;
    });
  });

  if (created > 0 || fixed) {
    if (created > 0) lg('cashflow', '—', `Back-filled ${created} salary cashTx record(s)`);
    save();
  }
  return created;
}

// Expense Definitions CRUD
function openExpDef(id){
  editExpDefId=id||null;
  const d=id?S.expenseDefs.find(x=>x.id===id):null;
  document.getElementById('edName').value=d?d.name:'';
  document.getElementById('edType').value=d?d.type:'Card';
  document.getElementById('edDay').value=d?d.day:'';
  document.getElementById('edFixed').value=d&&d.fixed?'1':'0';
  document.getElementById('edAmt').value=d&&d.fixedAmount?d.fixedAmount:'';
  document.getElementById('edLoanTotal').value=d&&d.loanTotal?d.loanTotal:'';
  document.getElementById('edLoanRemain').value=d&&d.loanRemain!=null?d.loanRemain:'';
  document.getElementById('edLoanStart').value=d&&d.loanStart?d.loanStart:'';
  document.getElementById('edLoanRate').value=d&&d.interestRate?d.interestRate:'';
  document.getElementById('edRecur').value=d?String(recurEveryOf(d)):'1';
  document.getElementById('edNote').value=d?d.note||'':'';
  document.getElementById('edDelBtn').style.display=id?'':'none';
  toggleEdLoan();
  document.getElementById('moExpDef').classList.add('active');
}

function toggleEdLoan(){
  const isFixed=document.getElementById('edFixed').value==='1';
  const isLoan=document.getElementById('edType').value==='Loan';
  document.getElementById('edAmtWrap').style.display=isFixed?'':'none';
  document.getElementById('edLoanWrap').style.display=isLoan?'':'none';
}

function saveExpDef(){
  const name=document.getElementById('edName').value.trim();
  if(!name)return toast('Name required','a-r');
  const day=parseInt(document.getElementById('edDay').value);
  if(!day||day<1||day>31)return toast('Valid day (1-31) required','a-r');
  const isFixed=document.getElementById('edFixed').value==='1';
  const fixedAmt=parseFloat(document.getElementById('edAmt').value)||0;
  if(isFixed&&fixedAmt<=0)return toast('Fixed amount must be positive','a-r');
  if(fixedAmt<0||!isFinite(fixedAmt))return toast('Invalid amount','a-r');
  const loanTotal=parseInt(document.getElementById('edLoanTotal').value)||0;
  const loanRemain=parseInt(document.getElementById('edLoanRemain').value)||0;
  if(loanRemain>loanTotal&&loanTotal>0)return toast('Remaining cannot exceed total loan months','a-r');
  const interestRate=parseFloat(document.getElementById('edLoanRate').value)||0;
  const d={
    name,type:document.getElementById('edType').value,day,
    fixed:isFixed,fixedAmount:fixedAmt,
    loanTotal,loanRemain,
    loanStart:document.getElementById('edLoanStart').value||null,
    // The period is stored even when it is 1, so an edit that brings a def BACK to monthly
    // actually clears a 12 rather than leaving it behind as a stale divisor.
    recurEvery:parseRecurEvery(document.getElementById('edRecur').value),
    interestRate,
    note:document.getElementById('edNote').value.trim()
  };
  // Saving is an owner attestation of the def's state: a Loan with months
  // remaining is alive again (month-close auto-completion may have set
  // active=false earlier; without this, editing loanRemain back up leaves
  // the def permanently dead with no UI trace), one with 0 stays done.
  if(d.type==='Loan')d.active=loanRemain>0;
  else d.active=true;
  // Stamped on edit as well as on create. `save()` only stamps a row that has NO stamp, so an
  // edited def kept its old `_updatedAt`, tied with its cloud twin on the next merge, and lost
  // (ties go to the cloud): an edited note or period could silently revert.
  if(editExpDefId){const idx=S.expenseDefs.findIndex(x=>x.id===editExpDefId);if(idx>=0)stampItem(Object.assign(S.expenseDefs[idx],d));}
  else S.expenseDefs.push(stampItem({id:uid(),...d,active:true}));
  editExpDefId=null;save();clMo('moExpDef');renderCF();
  if(cfActiveMo)renderCFModal();
  toast(name+' saved','a-b');
}

async function deleteExpDef(){
  if(!editExpDefId)return;if(!await uiConfirm('Delete this expense definition?',{title:'Delete expense definition',okText:'Delete',danger:true}))return;
  softDelete('expenseDefs',editExpDefId);
  editExpDefId=null;save();clMo('moExpDef');renderCF();
  if(cfActiveMo)renderCFModal();toast('Deleted','a-b');
}

// Income Definitions (deprecated since v0.7.1 — income is now per-month entries)
// Stub kept for any lingering references to avoid runtime errors.
function renderIncDefs(){ /* no-op: panel removed */ }

// === INCOME PROJECTION (trailing median of last N closed months) ===
function projectMonthlyIncome(n){
  n=n||3;
  const closedMonths=Object.keys(S.cashflow||{})
    .filter(k=>S.cashflow[k]&&S.cashflow[k].closed&&(S.cashflow[k].income||0)>0)
    .sort();
  if(closedMonths.length===0){
    // Fallback: last non-zero income if nothing closed
    const anyMonths=Object.keys(S.cashflow||{})
      .filter(k=>(S.cashflow[k].income||0)>0).sort();
    if(anyMonths.length===0)return 0;
    const last=S.cashflow[anyMonths[anyMonths.length-1]].income||0;
    return last;
  }
  const lastN=closedMonths.slice(-n);
  // One definition of "what repeats" — see recurringIncomeTRY. This used to carry its own
  // note-regex copy of the rule while _cfNeo's medInc carried none.
  const normals=lastN.map(k=>recurringIncomeTRY(S.cashflow[k])).filter(v=>v>0);
  if(normals.length===0)return 0;
  // Median
  const sorted=[...normals].sort((a,b)=>a-b);
  const mid=Math.floor(sorted.length/2);
  return sorted.length%2?sorted[mid]:(sorted[mid-1]+sorted[mid])/2;
}

// === EXPENSE PROJECTION (trailing median for a specific def) ===
// For variable (non-fixed) expense defs, compute trailing median from closed months
/** One spelling of a definition or item name, so id-matching and name-matching agree. */
function _cfName(x) { return String((x && x.name) || x || '').trim().toLowerCase(); }

function _projectExpenseForDef(defId, n) {
  n = n || 3;
  const closedMonths = Object.keys(S.cashflow || {})
    .filter(k => S.cashflow[k] && S.cashflow[k].closed)
    .sort();
  // A definition created after the fact has no id in the months that came before it. The four
  // cards (v44) were typed by hand for four months with defId:null, so matching on the id
  // alone would return 0 for them and forwardEssentialMonthlyTRY — the reserve target's whole
  // basis — would drop the cards on the floor. Fall back to the name for exactly those rows.
  // (audit 2026-08-04)
  const def = (S.expenseDefs || []).find(d => d && d.id === defId);
  const dName = _cfName(def);
  const amounts = [];
  closedMonths.forEach(k => {
    const mo = S.cashflow[k];
    if (!mo?.items?.length) return;
    // Karma is per-ITEM, not per-definition. April 2026 is the proof: the mortgage was
    // paidBy:'income' that month (he paid it himself) while the ₺290,874 Bonus card bill was
    // paidBy:'mixed' — the opposite of every other month. A definition-level karma flag
    // cannot express that, so this has to read the row. Without it a month somebody else
    // paid would enter the forward projection as his, which is the same defect the rest of
    // this file was just cleared of. (audit 2026-08-04)
    // `oneOff` excluded here as well as in the median below. A card month that was a one-off by
    // definition (a single large purchase put on a card) must not raise the card's own trailing
    // median either, or the one-off comes back through the other door.
    const usable = it => it && !isKarma(it) && it.confirmed && it.amount > 0 && recurEveryOf(it) !== 0;
    const item = mo.items.find(it => it.defId === defId && usable(it))
      || (dName ? mo.items.find(it => !it.defId && _cfName(it) === dName && usable(it)) : null);
    if (item) amounts.push(item.amount);
  });
  if (!amounts.length) return 0;
  const lastN = amounts.slice(-n);
  const sorted = [...lastN].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Anchor month for loanRemain counting: the first month not yet closed.
// loanRemain is decremented on month close, so once the current calendar
// month is closed the remaining installments start at the NEXT month —
// anchoring on wall-clock "now" would wrongly exclude the final month.
function cfLoanAnchor(){
  const n=new Date();
  let y=n.getFullYear(),m=n.getMonth()+1;
  for(let i=0;i<12;i++){
    const key=y+'-'+String(m).padStart(2,'0');
    if(!(S.cashflow&&S.cashflow[key]&&S.cashflow[key].closed))break;
    m++;if(m>12){m=1;y++;}
  }
  return [y,m];
}

/**
 * HOW OFTEN, IN MONTHS. The file had no way to say it until 2026-10-03.
 *
 *   absent  every month (the default, and what every pre-existing row means)
 *   0       never again — this is what `oneOff` meant, and `oneOff: true` is still read as 0
 *           for one version so a device that has not migrated does not lose the flag on a merge
 *   N > 1   every N months. The forward basis counts `amount / N`; the month it falls in still
 *           carries the whole amount, because that is the money that actually leaves.
 *
 * The school fee is the case that forced it: TRY 150,000 a year, billed as two TRY 75,000
 * installments in September and October. Two definitions at `recurEvery: 12`, anchored a month
 * apart, amortise to 6,250 each — TRY 12,500/month — and bill 75,000 in their own months. One
 * field expresses both, which a single `annual: true` flag could not.
 */
/**
 * The form's "Bills every (months)" field, as a period. `parseInt(v)||1` was the previous
 * spelling and it could never say 0: `parseInt('0')` is 0, and `0||1` is 1. A one-off typed
 * into the form was saved as monthly, which is the opposite of what was typed. Empty is monthly;
 * a number is itself, floored at 0.
 */
function parseRecurEvery(v){
  const n=parseInt(v);
  return Number.isFinite(n)?Math.max(0,n):1;
}

function recurEveryOf(x){
  if(!x)return 1;
  if(x.oneOff===true)return 0;                       // legacy spelling, defa1c7
  const r=x.recurEvery;
  // ⚠ `null` IS ABSENT, NOT ZERO. `Number(null)` is 0 and 0 means "never again", so a row whose
  // period was cleared to null would have dropped out of the reserve basis entirely instead of
  // returning to monthly. Only an explicit numeric 0 retires a row.
  if(r===null||r===undefined||r==='')return 1;
  const n=Number(r);
  if(n===0)return 0;
  if(!isFinite(n)||n<0)return 1;
  return Math.max(1,Math.round(n));
}

/**
 * Does a definition BILL in this month? Monthly defs always do. A periodic one bills on its
 * anchor and every `recurEvery` months after it, so eleven months of the year an annual fee
 * generates no row at all instead of an empty one. `loanStart` (YYYY-MM) is the anchor — the
 * spelling already in the file — and without one a periodic def falls back to billing every
 * month rather than guessing a cycle it was never told.
 */
function defDueForMonth(d,y,m){
  const n=recurEveryOf(d);
  if(n===0)return false;
  if(n===1)return true;
  const a=/^(\d{4})-(\d{2})$/.exec(String((d&&d.loanStart)||''));
  if(!a)return true;
  const diff=(y-Number(a[1]))*12+(m-Number(a[2]));
  return diff>=0&&diff%n===0;
}

// Check if a definition is active for a given month
function defActiveForMonth(d,y,m){
  if(d.active===false)return false;
  if(d.type==='Loan'){
    // Primary: loanRemain — most reliable, user-maintained
    if(d.loanRemain!=null){
      if(d.loanRemain<=0)return false; // explicitly completed
      const [ay,am]=cfLoanAnchor();
      const monthsAhead=(y-ay)*12+(m-am);
      if(monthsAhead>=d.loanRemain)return false;
    }
    // Fallback: loanStart (YYYY-MM) + loanTotal — absolute end date
    else if(d.loanStart&&d.loanTotal&&/^\d{4}-\d{2}$/.test(d.loanStart)){
      const sp=d.loanStart.split('-').map(Number);
      const elapsed=(y-sp[0])*12+(m-sp[1]);
      if(elapsed<0)return false;
      if(elapsed>=d.loanTotal)return false;
    }
  }
  return true;
}

// Generate items from definitions for a month
function cfGenItems(k){
  const mo=cfGetMonth(k);
  if(mo.closed)return;
  const [y,m]=k.split('-').map(Number);
  S.expenseDefs.filter(d=>defActiveForMonth(d,y,m)&&defDueForMonth(d,y,m)).forEach(d=>{
    if(!mo.items.find(it=>it.defId===d.id)){
      mo.items.push({defId:d.id,day:d.day,type:d.type,name:d.name,amount:d.fixed?d.fixedAmount:null,confirmed:false,note:'',...(d.paidBy?{paidBy:d.paidBy}:{}),...(recurEveryOf(d)!==1?{recurEvery:recurEveryOf(d)}:{})});
    }
  });
  mo.items.sort((a,b)=>a.day-b.day);
}

// Open month modal (new or edit)
function openCFMonth(k){
  if(!k){
    const now2=new Date();
    k=now2.getFullYear()+'-'+String(now2.getMonth()+1).padStart(2,'0');
  }
  cfActiveMo=k;
  // Populate month selector
  const sel=document.getElementById('cfMoSel');
  const existing=Object.keys(S.cashflow).sort();
  const months=new Set([...existing,k]);
  // Add next 3 months from now
  const now2=new Date();
  for(let i=0;i<3;i++){
    const d=new Date(now2.getFullYear(),now2.getMonth()+i);
    months.add(d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0'));
  }
  const sorted=[...months].sort();
  sel.innerHTML=sorted.map(m=>`<option value="${m}" ${m===k?'selected':''}>${m}</option>`).join('');
  const mo=cfGetMonth(k);
  cfGenItems(k);
  // Default portfolio commitment = base USD × multiplier × current FX rate
  if(!mo.portfolioComm&&S.base&&S.fxRate){
    const mr=S.monthlyRates[k];const mul=mr?.mul||1;
    mo.portfolioComm=Math.round(S.base*mul*S.fxRate);
  }
  cfRecalcIncome(mo);
  document.getElementById('cfIncome').value=mo.income||'';
  document.getElementById('cfPortComm').value=mo.portfolioComm||'';
  // Never auto-filled: no formula in this app can predict recycled capital, and a default
  // here would be the same plug in a different box.
  { const _rd=document.getElementById('cfRedeployed'); if(_rd)_rd.value=mo.redeployed||''; }
  renderCFModal();
  document.getElementById('moCF').classList.add('active');
}

function cfMoChanged(){
  const k=document.getElementById('cfMoSel').value;
  cfActiveMo=k;
  const mo=cfGetMonth(k);
  cfGenItems(k);
  if(!mo.portfolioComm&&S.base&&S.fxRate){
    // Pull actual multiplier from reconciliation if available
    const mr=S.monthlyRates[k];
    const mul=mr?.mul||1;
    mo.portfolioComm=Math.round(S.base*mul*S.fxRate);
  }
  cfRecalcIncome(mo);
  document.getElementById('cfIncome').value=mo.income||'';
  document.getElementById('cfPortComm').value=mo.portfolioComm||'';
  { const _rd=document.getElementById('cfRedeployed'); if(_rd)_rd.value=mo.redeployed||''; }
  renderCFModal();
}

// ===== PER-MONTH INCOME ENTRIES =====
function cfAddIncome(){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  if(mo.closed)return toast('Month is closed','a-r');
  mo.incomes.push({id:uid(),day:1,amount:0,type:'Salary',note:''});
  cfRecalcIncome(mo);
  save();renderCFIncome();
}

function cfRemoveIncome(idx){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  if(mo.closed)return toast('Month is closed','a-r');
  const entry = mo.incomes[idx];
  if(entry && entry.id) _removeIncomeTx(entry.id);
  mo.incomes.splice(idx,1);
  cfRecalcIncome(mo);
  save();renderCFIncome();cfCalcSummary();renderCF();
}

function cfSetIncome(e){
  const args=e.target.getAttribute('data-args');
  const parsed=args?JSON.parse(args):[];
  const idx=parsed[0],field=parsed[1],val=e.target.value;
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  if(!mo.incomes[idx])return;
  if(field==='amount'||field==='day')mo.incomes[idx][field]=parseFloat(val)||0;
  else mo.incomes[idx][field]=val;
  cfRecalcIncome(mo);
  document.getElementById('cfIncome').value=mo.income||'';
  cfIncomeFooter(mo);
  save();cfCalcSummary();
}

function cfIncomeFooter(mo){
  const tot=mo.income||0;
  const oneOff=oneOffIncomeTRY(mo);
  const el1=document.getElementById('cfIncomeTotTRY');
  const el2=document.getElementById('cfIncomeTotUSD');
  if(el1)el1.textContent=fT(tot);
  // A classification nobody can see is a classification nobody checks.
  if(el2)el2.innerHTML=(tot?('≈ '+fU(toUSDStrict(tot, 'TRY'))):'')
    +(oneOff>0?` · <span style="color:var(--pur)">${fT(oneOff)} one-off — not projected forward</span>`:'');
}

function renderCFIncome(){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  const tb=document.getElementById('cfIncomeBody');
  if(!tb)return;
  const isClosed=mo.closed;
  if(!mo.incomes||mo.incomes.length===0){
    tb.innerHTML='<tr><td colspan="5" class="neo-empty">No income entries. Click "+ Add Income" to record salary or other receipts.</td></tr>';
  } else {
    tb.innerHTML=mo.incomes.map((e,idx)=>{
      const disabled=isClosed?'disabled':'';
      return `<tr>
        <td><input type="number" min="1" max="31" value="${e.day||1}" ${disabled} style="width:46px" data-action-change="cfSetIncome" data-args='[${idx},"day"]'></td>
        <td class="tr"><input type="number" step="any" value="${e.amount||''}" ${disabled} style="width:100%;text-align:right" data-action-change="cfSetIncome" data-args='[${idx},"amount"]' placeholder="0"></td>
        <td><select ${disabled} data-action-change="cfSetIncome" data-args='[${idx},"type"]' style="width:100%;font-size:11px">
          <option value="Salary" ${e.type==='Salary'?'selected':''}>Salary</option>
          <option value="Bonus" ${e.type==='Bonus'?'selected':''}>Bonus</option>
          <option value="Commission" ${e.type==='Commission'?'selected':''}>Commission</option>
          <option value="Other" ${e.type==='Other'?'selected':''}>Other</option>
        </select></td>
        <td><input type="text" value="${esc(e.note||'')}" ${disabled} style="width:100%" data-action-change="cfSetIncome" data-args='[${idx},"note"]' placeholder="e.g. Bonus, Freelance"></td>
        <td>${isClosed?'':`<button class="btn btn-s btn-d" style="font-size:10px;padding:2px 6px" data-action="cfRemoveIncome" data-args='[${idx}]'>×</button>`}</td>
      </tr>`;
    }).join('');
  }
  cfIncomeFooter(mo);
  document.getElementById('cfIncome').value=mo.income||'';
}

// Render modal content
function renderCFModal(){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  const isClosed=mo.closed;
  const mNames=['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [yy,mm]=k.split('-').map(Number);
  document.getElementById('cfModTitle').textContent=mNames[mm]+' '+yy+' — Expenses'+(isClosed?' (Closed)':'');
  document.getElementById('cfCloseMonthBtn').style.display=isClosed?'none':'';

  // Build day rows (1-31)
  const daysInMonth=new Date(yy,mm,0).getDate();
  let rows='';
  for(let day=1;day<=daysInMonth;day++){
    // Find items for this day
    const dayItems=mo.items.filter(it=>it.day===day);
    if(dayItems.length===0){
      // Empty day row — just show day number + add button
      rows+=`<tr style="opacity:.4"><td>${day}</td><td colspan="5" style="color:var(--t3);font-size:11px">—</td><td></td>
        <td>${isClosed?'':` <button class="btn btn-s" style="font-size:9px;padding:1px 6px" data-action="cfAddItem" data-args='[${day}]'>+</button>`}</td></tr>`;
    }else{
      dayItems.forEach((it,i)=>{
        const idx=mo.items.indexOf(it);
        const def=it.defId?S.expenseDefs.find(d=>d.id===it.defId):null;
        const name=def?def.name:(it.name||'');
        const type=it.type||(def?def.type:'Other');
        const isEditable=!it.defId&&!it.confirmed&&!isClosed;
        const badgeStatic=type==='Loan'?'<span class="badge" style="background:var(--yel-bg);color:var(--yel);font-size:10px">Loan</span>':
                     type==='Card'?'<span class="badge" style="background:rgba(167,139,250,.2);color:var(--pur);font-size:10px">Card</span>':
                     '<span class="badge gls-soft" style="color:var(--t2);font-size:10px">Other</span>';
        const badgeEdit=`<select style="font-size:10px;width:60px" data-action-change="cfSetField" data-args='[${idx},"type"]'>
          <option value="Card" ${type==='Card'?'selected':''}>Card</option>
          <option value="Loan" ${type==='Loan'?'selected':''}>Loan</option>
          <option value="Other" ${type==='Other'?'selected':''}>Other</option></select>`;
        const badge=isEditable?badgeEdit:badgeStatic;
        const amtUSD=it.amount?toUSDStrict(it.amount,'TRY'):0;   // null -> fU renders an em dash

        let amtCell='',statusCell='',actCell='';
        if(isClosed||it.confirmed){
          amtCell=it.amount?fT(it.amount):'—';
          statusCell='<span style="color:var(--grn);font-size:11px">✓ Paid</span>';
          actCell=isClosed?'':`<button class="btn btn-s" style="font-size:9px" data-action="cfToggle" data-args='[${idx},false]'>Undo</button>`;
        }else{
          amtCell=`<input type="number" id="cfA_${idx}" style="width:110px" value="${it.amount||''}" step="any" placeholder="TRY" data-action-change="cfSetAmt" data-args='[${idx}]'>`;
          statusCell='<span style="color:var(--yel);font-size:11px">Pending</span>';
          actCell=`<button class="btn btn-s btn-p" style="font-size:9px" data-action="cfToggle" data-args='[${idx},true]'>Done</button>
            <button class="btn btn-s btn-d" style="font-size:9px;margin-left:2px" data-action="cfRemoveItem" data-args='[${idx}]'>✕</button>`;
        }

        // Show day number only on first item for that day
        const dayCell=i===0?`<strong>${day}</strong>`:'';
        const nameCell=isEditable?
          `<input id="cfN_${idx}" style="width:80px;font-size:11px" value="${esc(name)}" data-action-change="cfSetField" data-args='[${idx},"name"]'>`
          :esc(name);

        // Paid By selector
        const pb=it.paidBy||'income';
        const pbSrc=it.paidBySource||'';
        let paidByCell='';
        if(isClosed||it.confirmed){
          const pbLabel=pb==='income'?'Income':pb==='other'?'Other':pb==='savings'?(pbSrc==='cash'?'Cash':pbSrc==='td'?'Time Deposit':pbSrc==='reserve'?'Reserve Fund':'Savings'):'Karma';
          paidByCell=`<span style="font-size:10px">${pbLabel}</span>`;
        }else{
          paidByCell=`<select style="font-size:10px;width:70px" id="cfPB_${idx}" data-action-change="cfPaidByChanged" data-args='[${idx}]'>
            <option value="income" ${pb==='income'?'selected':''}>Income</option>
            <option value="savings" ${pb==='savings'?'selected':''}>Savings</option>
            <option value="other" ${pb==='other'?'selected':''}>Other</option>
            <option value="mixed" ${pb==='mixed'?'selected':''}>Karma</option>
          </select>${pb==='savings'?`<select style="font-size:10px;width:70px;margin-left:2px" id="cfPBS_${idx}" data-action-change="cfSetField" data-args='[${idx},"paidBySource"]'>
            <option value="cash" ${pbSrc==='cash'?'selected':''}>Cash</option>
            <option value="td" ${pbSrc==='td'?'selected':''}>Time Dep.</option>
            <option value="reserve" ${pbSrc==='reserve'?'selected':''}>Reserve</option>
          </select>`:''}`;
        }

        // ONE-OFF. Rides beside Paid by because it answers the neighbouring question: Paid by says
        // where the money came from, this says whether it will come again. Only the forward basis
        // reads it (`forwardEssentialMonthlyTRY`), so ticking it changes no total on this page.
        // Shown as a static mark once the row is confirmed or the month is closed, like Paid by.
        const oneOffCell = (isClosed || it.confirmed)
          ? (recurEveryOf(it) === 0 ? '<span style="font-size:10px;color:var(--t3)" title="Excluded from the forward essential basis">one-off</span>'
             : (recurEveryOf(it) > 1 ? `<span style="font-size:10px;color:var(--t3)" title="Counted in the forward basis at a ${recurEveryOf(it)}th of its amount">every ${recurEveryOf(it)}mo</span>` : ''))
          : `<label style="font-size:10px;color:var(--t3);cursor:pointer;white-space:nowrap" title="Paid once. Stays in this month\u2019s plan; kept out of the forward essential basis the reserve target is built from."><input type="checkbox" id="cfOO_${idx}" ${recurEveryOf(it) === 0 ? 'checked' : ''} data-action-change="cfOneOffChanged" data-args='[${idx}]' style="margin-right:3px;vertical-align:middle">once</label>`;

        rows+=`<tr style="${it.confirmed?'':'background:rgba(245,158,11,.03)'}">
          <td>${dayCell}</td><td>${badge}</td><td>${nameCell}</td>
          <td class="tr">${amtCell}</td><td class="tr">${it.amount?fU(amtUSD):'—'}</td>
          <td>${paidByCell}${oneOffCell ? '<div style="margin-top:2px">' + oneOffCell + '</div>' : ''}</td>
          <td>${statusCell}</td><td>${actCell}</td></tr>`;
      });
      // Add more button for this day (if not closed)
      if(!isClosed){
        rows+=`<tr style="opacity:.3"><td></td><td colspan="6"></td>
          <td><button class="btn btn-s" style="font-size:9px;padding:1px 6px" data-action="cfAddItem" data-args='[${day}]'>+</button></td></tr>`;
      }
    }
  }
  document.getElementById('cfDayBody').innerHTML=rows;

  // Totals
  // paidTotal used to include karma while every sibling figure excluded it, so the modal's
  // Paid tile disagreed with the Paid tile two rows below it by the whole mortgage.
  const paidTotal=monthOutflowTRY(mo,true);
  const projTotal=monthOutflowTRY(mo);
  const confCount=mo.items.filter(it=>it.confirmed).length;
  const totalCount=mo.items.length;
  const unknownCount=mo.items.filter(it=>!it.amount&&!it.confirmed).length;
  document.getElementById('cfModPaidTRY').textContent=fT(paidTotal);
  document.getElementById('cfModPaidUSD').textContent=fU(toUSDStrict(paidTotal, 'TRY'));
  document.getElementById('cfModPaidCount').textContent=confCount+'/'+totalCount+' items';
  document.getElementById('cfModProjTRY').textContent=fT(projTotal);
  document.getElementById('cfModProjUSD').textContent=fU(toUSDStrict(projTotal, 'TRY'));
  document.getElementById('cfModProjNote').textContent=unknownCount>0?unknownCount+' items TBD':'All amounts set';

  // Progress bar
  const confirmed=mo.items.filter(it=>it.confirmed).length;
  const total=mo.items.length;
  const pct=total>0?(confirmed/total)*100:0;
  document.getElementById('cfProg').style.width=pct+'%';
  document.getElementById('cfProgTxt').textContent=confirmed+'/'+total+' ('+Math.round(pct)+'%)';

  // Render per-month income entries
  renderCFIncome();
  cfCalcSummary();
}

function cfCalcSummary(){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  cfRecalcIncome(mo);
  const income=mo.income||0;
  const portComm=parseFloat(document.getElementById('cfPortComm').value)||0;
  const redep=Math.max(0,parseFloat(document.getElementById('cfRedeployed')?.value)||0);
  // Paid = confirmed items only (Karma/mixed excluded from financial impact)
  const paid=monthOutflowTRY(mo,true);
  const paidKarma=monthKarmaTRY(mo,true);
  // Paid by source breakdown
  const paidByIncome=mo.items.reduce((s,it)=>s+(it.confirmed&&it.amount&&(!it.paidBy||it.paidBy==='income')?it.amount:0),0);
  const paidBySavings=mo.items.reduce((s,it)=>s+(it.confirmed&&it.amount&&it.paidBy==='savings'?it.amount:0),0);
  // Projected = all items that have an amount (Karma excluded)
  const projected=monthOutflowTRY(mo);
  const projByIncome=mo.items.reduce((s,it)=>s+(it.amount&&(!it.paidBy||it.paidBy==='income')?it.amount:0),0);
  const projKarma=monthKarmaTRY(mo);
  const unknown=mo.items.filter(it=>!it.amount&&!it.confirmed).length;
  const disposable=income-portComm;
  const surplusProj=disposable-projByIncome; // surplus only considers income-paid items
  const confirmed=mo.items.filter(it=>it.confirmed).length;
  const total=mo.items.length;
  document.getElementById('cfModalSum').innerHTML=`
    <div class="neo-cell flat"><div class="l">Income</div><div class="v">${fT(income)}</div><div class="s">${fU(toUSDStrict(income, 'TRY'))}</div></div>
    <div class="neo-cell flat"><div class="l">Portfolio</div><div class="v">${fT(portComm)}</div><div class="s">${fU(toUSDStrict(portComm, 'TRY'))}${redep?' · +'+fT(redep)+' recycled → '+fT(portComm+redep)+' deployed':''}</div></div>
    <div class="neo-cell flat"><div class="l">Disposable</div><div class="v">${fT(disposable)}</div><div class="s">${fU(toUSDStrict(disposable, 'TRY'))}</div></div>
    <div class="neo-cell flat"><div class="l">Paid (${confirmed}/${total})</div><div class="v" style="color:var(--red)">${fT(paid)}</div><div class="s">Income: ${fT(paidByIncome)}${paidBySavings?' · Savings: '+fT(paidBySavings):''}${paidKarma?' · <span style="color:var(--pur)">Karma: '+fT(paidKarma)+'</span>':''}</div></div>
    <div class="neo-cell flat"><div class="l">Projected${unknown?' <span style="color:var(--yel)">+'+unknown+' TBD</span>':''}</div><div class="v" style="color:var(--ora)">${fT(projected)}${projKarma?' <span style="font-size:10px;color:var(--pur)">+'+fT(projKarma)+' karma</span>':''}</div><div class="s">Est. Surplus: <span class="${surplusProj>=0?'pos':'neg'}">${fT(surplusProj)}</span></div></div>`;
}

// Item actions
/**
 * EVERY MUTATION IN THIS MODAL PERSISTS. It did not, and that is how three card payments
 * entered on 2026-08-03 became one.
 *
 * The modal treated its rows as a draft: cfAddItem pushed into S.cashflow[k].items and
 * re-rendered, cfSetField wrote the name, cfSetAmt wrote the amount, cfAddIncome pushed an
 * income row — and NONE of them called save(). Only cfToggle (mark paid), cfRemoveItem and
 * the two Save buttons did. The owner marked the Axess payment paid, so that one went
 * through cfToggle and survived; Bonus and Pgs were entered as upcoming, lived in memory
 * until Esc or a backdrop click closed the modal, and then did not exist. No error, no
 * toast, and — see the lg() calls below — no history entry to find them by either.
 *
 * save() writes IDB and localStorage immediately and debounces the Supabase upsert by
 * CLOUD_DEBOUNCE_MS behind a hash-skip, so persisting per field change adds no cloud write.
 * A draft that can evaporate is not worth the bytes it saves. (audit 2026-08-04)
 */
function cfAddItem(day){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  if(mo.closed)return;
  mo.items.push({defId:null,day,type:'Other',name:'',amount:null,confirmed:false,note:''});
  mo.items.sort((a,b)=>a.day-b.day);
  save();lg('cashflow',k,'Added item on day '+day);renderCFModal();
}

/**
 * Read back whatever is sitting in the modal's inputs and persist it.
 *
 * The per-field handlers fire on `change`, which is a blur event. Esc and a backdrop click
 * close the modal without necessarily blurring the focused input first, so a freshly typed
 * amount could still be lost with everything else already fixed. layout.js announces every
 * modal close; this is the listener for ours.
 */
function cfFlushOpenMonth(){
  const k=cfActiveMo;if(!k)return;
  const mo=S.cashflow&&S.cashflow[k];
  if(!mo||mo.closed||!Array.isArray(mo.items))return;
  let dirty=false;
  mo.items.forEach((it,idx)=>{
    if(it.confirmed)return;
    const amt=document.getElementById('cfA_'+idx);
    if(amt&&amt.value!==''){
      const v=parseFloat(amt.value);
      if(!isNaN(v)&&v!==it.amount){it.amount=v;dirty=true;}
    }
    const nm=document.getElementById('cfN_'+idx);
    if(nm&&nm.value!==(it.name||'')){it.name=nm.value;dirty=true;}
  });
  if(dirty){save();lg('cashflow',k,'Flushed unsaved edits on modal close');}
}

if(typeof document!=='undefined'){
  document.addEventListener('zagx:modal-close',e=>{
    try{if(e&&e.detail&&e.detail.id==='moCF')cfFlushOpenMonth();}catch(_e){/* optional */}
  });
}

function cfRemoveItem(idx){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  const gone=mo.items[idx];
  if(!gone)return;
  // TWO GUARDS ITS SIBLING ALREADY HAD, OR HALF HAD.
  //
  // The Cash page badges an `Expense:` row "Cash Flow → the month". The control that badge
  // means is Undo — `cfToggle(idx,false)` — which returns the money and writes the compensating
  // `Undo expense:` row. This ✕ sits next to it and reverses NOTHING: it splices the item, and
  // a cash-funded expense would leave `S.cash.TRY` down with its ledger row pointing at a month
  // that no longer contains the item. The money genuinely left, so no balance check would see
  // it (B3 stays green) — the loss is the record of WHY, and the next re-add and re-tick debits
  // the same bill a second time.
  //
  // The render already hides ✕ for a confirmed item and for a closed month, so neither is
  // reachable through the page today. Both are enforced HERE as well, for the reason MOVE-003
  // gave when `delCashTx` grew its own guard: a `data-action` can be dispatched from anywhere,
  // and this is the function that fails to move the balance. `cfRemoveIncome` already refuses
  // on a closed month; this one did not. (audit 2026-08-14, the door sweep, part three)
  if(mo.closed)return toast('Month is closed','a-r');
  if(gone._deducted>0)return toast(
    'This item was paid from '+(gone._deductedFrom==='reserve'?'the Reserve Fund':'Cash TRY')
    +' (₺'+f(gone._deducted)+'). Press Undo first — that returns the money and records the reversal.','a-r');
  mo.items.splice(idx,1);
  save();
  if(gone)lg('cashflow',k,'Removed '+(gone.name||gone.type||'item')+' (day '+gone.day+')'+(gone.amount?' ₺'+f(gone.amount):''));
  renderCFModal();
}

function cfSetAmt(e){
  const args=e.target.getAttribute('data-args');
  const parsed=args?JSON.parse(args):[];
  const idx=parsed[0],val=e.target.value;
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  if(mo.items[idx])mo.items[idx].amount=parseFloat(val)||null;
  save();cfCalcSummary();
}

function cfSetField(e){
  const args=e.target.getAttribute('data-args');
  const parsed=args?JSON.parse(args):[];
  const idx=parsed[0],field=parsed[1],val=e.target.value;
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  if(mo.items[idx])mo.items[idx][field]=val;
  save();
}

// A checkbox's `.value` is the string "on" whatever its state, so `cfSetField` cannot carry this
// one — it would write "on" and never write false. Its own handler reads `.checked`, and deletes
// the key rather than storing `false`, so an ordinary row keeps the shape it has always had.
function cfOneOffChanged(e){
  const args=e.target.getAttribute('data-args');
  const parsed=args?JSON.parse(args):[];
  const idx=parsed[0];
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  const it=mo.items[idx];if(!it)return;
  if(e.target.checked)it.recurEvery=0;else delete it.recurEvery;
  delete it.oneOff;                       // the legacy spelling never co-exists with the new one
  lg('cashflow','One-off',(it.name||'item')+' '+(it.oneOff?'excluded from':'returned to')+' the forward essential basis');
  // markDirty('dash') as well as the modal: this is the one row-level flag that moves a figure
  // OFF this page — the reserve target, and through it the Goals stage.
  save();markDirty('dash');renderCFModal();
}

function cfPaidByChanged(e){
  const args=e.target.getAttribute('data-args');
  const parsed=args?JSON.parse(args):[];
  const idx=parsed[0],val=e.target.value;
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  const it=mo.items[idx];if(!it)return;
  it.paidBy=val;
  if(val==='savings'&&!it.paidBySource)it.paidBySource='cash';
  if(val!=='savings')it.paidBySource='';
  save();renderCFModal();
}

function cfToggle(idx,state){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  const it=mo.items[idx];if(!it)return;
  if(state){
    // Read amount from input if not yet set
    const inp=document.getElementById('cfA_'+idx);
    if(inp)it.amount=parseFloat(inp.value)||0;
    if(!it.amount)return toast('Enter amount first','a-r');
    // Read paid-by from dropdown if present
    const pbSel=document.getElementById('cfPB_'+idx);
    if(pbSel)it.paidBy=pbSel.value;
    if(it.paidBy==='savings'){
      const srcSel=document.getElementById('cfPBS_'+idx);
      if(srcSel)it.paidBySource=srcSel.value;
    }
    // Deduct from savings balance if applicable
    if(it.paidBy==='savings'&&it.paidBySource){
      const amt=it.amount;
      if(it.paidBySource==='cash'){
        if((S.cash.TRY||0)<amt)return toast('Insufficient TRY cash (₺'+f(S.cash.TRY||0)+' available)','a-r');
        S.cash.TRY-=amt;
        S.cashTxs.unshift({id:uid(),date:now(),type:'withdraw',amount:-amt,currency:'TRY',note:'Expense: '+it.name+' ('+k+')'});
        lg('cash','Expense','Paid from Cash TRY: ₺'+f(amt)+' — '+it.name);
      } else if(it.paidBySource==='reserve'){
        const _rfBal = (S.reserveFund?.TRY || (typeof S.reserveFund === 'number' ? S.reserveFund : 0));
        if(_rfBal<amt)return toast('Insufficient reserve fund TRY (₺'+f(_rfBal)+' available)','a-r');
        if(typeof S.reserveFund === 'object') S.reserveFund.TRY -= amt; else S.reserveFund -= amt;
        S.rfTxs.unshift({id:uid(),date:now(),type:'withdraw',amount:-amt,note:'Expense: '+it.name+' ('+k+')'});
        lg('cash','Expense','Paid from Reserve Fund: ₺'+f(amt)+' — '+it.name);
      }
      // td = time deposit → user handles manually, no auto-deduction
    }
    it._deducted=it.paidBy==='savings'&&(it.paidBySource==='cash'||it.paidBySource==='reserve')?it.amount:0;
    it._deductedFrom=it._deducted?it.paidBySource:null;
  } else {
    // Undo — reverse any prior deduction
    if(it._deducted&&it._deducted>0){
      // Read the source that was RECORDED at deduction time, not paidBySource — an 'income'
      // row has no paidBySource and would otherwise reverse nothing while still clearing the flag.
      const _src=it._deductedFrom||it.paidBySource;
      if(_src==='cash'){
        S.cash.TRY+=it._deducted;
        S.cashTxs.unshift({id:uid(),date:now(),type:'deposit',amount:it._deducted,currency:'TRY',note:'Undo expense: '+it.name+' ('+k+')'});
      } else if(_src==='reserve'){
        if(typeof S.reserveFund === 'object') S.reserveFund.TRY += it._deducted; else S.reserveFund += it._deducted;
        S.rfTxs.unshift({id:uid(),date:now(),type:'deposit',amount:it._deducted,note:'Undo expense: '+it.name+' ('+k+')'});
      }
    }
    delete it._deducted;delete it._deductedFrom;
  }
  it.confirmed=state;
  // WHEN the money moved, not when it was due. The anchor asks "has this left the account
  // since the last bank reading", and a bill dated the 11th ticked on the 10th answered that
  // question with the 11th — so reconciling on the 10th, with the payment already inside the
  // balance he typed, subtracted it a second time. The due date is not a payment date.
  // ONCE, and kept. Re-stamping on every tick meant that un-ticking a row to fix its amount
  // and re-ticking it moved the payment forward past the last reconciliation, and the same
  // money came out of the balance a second time. Un-ticking does not clear it either: the row
  // was paid when it was paid, and the stamp is only ever consulted for a confirmed row.
  if(state&&!it.confirmedAt)it.confirmedAt=now();
  save();
  // Ticking a bill changes the derived TRY figure on the Cash page (projectedCashTRY reads
  // `confirmed`). markDirty('cf') carries the implication to 'cash' — see render-loop.js.
  markDirty('cf');
  lg('cashflow',k,(state?'Confirmed ':'Reopened ')+(it.name||it.type||'item')+' (day '+it.day+') ₺'+f(it.amount||0));
  renderCFModal();
}

// Save month from modal
function saveCFMo(){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  cfRecalcIncome(mo);
  mo.portfolioComm=parseFloat(document.getElementById('cfPortComm').value)||0;
  // Only when the input is actually on the page. A stale cached index.html against fresh JS
  // would otherwise read `undefined` and write 0 over a value the owner had entered — the
  // field would erase itself on the next Save with nothing to show for it.
  const _rd=document.getElementById('cfRedeployed');
  if(_rd)mo.redeployed=Math.max(0,parseFloat(_rd.value)||0);
  // Read all pending amounts from inputs
  mo.items.forEach((it,idx)=>{
    if(!it.confirmed){const inp=document.getElementById('cfA_'+idx);if(inp&&inp.value)it.amount=parseFloat(inp.value)||null;}
  });
  // Sync income entries → cashTx deposits (single save() call below covers everything)
  _syncMonthIncomeTxs(k);
  save();
  lg('cashflow',k,'Saved '+mo.items.length+' item(s) · income ₺'+f(mo.income||0));
  markDirty('cf');renderCFModal();renderCF();toast('Saved','a-b');
}

// Close month
async function closeCFMonth(){
  const k=cfActiveMo;if(!k)return;
  const mo=cfGetMonth(k);
  const unconf=mo.items.filter(it=>!it.confirmed).length;
  if(unconf>0&&!await uiConfirm(unconf+' unconfirmed items. Close month anyway?',{title:'Close month',okText:'Close anyway'}))return;
  cfRecalcIncome(mo);
  mo.portfolioComm=parseFloat(document.getElementById('cfPortComm').value)||0;
  // Only when the input is actually on the page. A stale cached index.html against fresh JS
  // would otherwise read `undefined` and write 0 over a value the owner had entered — the
  // field would erase itself on the next Save with nothing to show for it.
  const _rd=document.getElementById('cfRedeployed');
  if(_rd)mo.redeployed=Math.max(0,parseFloat(_rd.value)||0);
  // Final sync of income → cashTx before closing
  _syncMonthIncomeTxs(k);
  mo.closed=true;
  // Decrement loanRemain for active loans on month close (only if active for this month)
  const [_cY,_cM]=[parseInt(k.slice(0,4)),parseInt(k.slice(5,7))];
  S.expenseDefs.filter(d=>d.type==='Loan'&&d.active!==false&&d.loanRemain>0&&defActiveForMonth(d,_cY,_cM)).forEach(d=>{
    d.loanRemain--;
    if(d.loanRemain<=0){d.active=false;lg('expense',d.name,'Loan completed (remain→0)');}
    stampItem(d);
  });
  save();markDirty('cf');renderCFModal();renderCF();toast('Month closed','a-g');
  lg('cashflow','—','Closed month '+k);
}

// Delete entire month
async function cfDeleteMonth(k){
  if(!k||!S.cashflow[k])return;
  if(!await uiConfirm('Delete month '+k+'? All items will be lost.',{title:'Delete month',okText:'Delete',danger:true}))return;
  delete S.cashflow[k];
  save();renderCF();toast(k+' deleted','a-b');
}

// Render main page
function _cfNeo(){
  const $=id=>document.getElementById(id);
  if(!$('cfwSurplus'))return;
  const keys=Object.keys(S.cashflow||{}).sort();
  // `inc` is what actually arrived — the bars must show the real month, bonus and all.
  // `rec` is what repeats, and it is the only one the forward median may touch.
  const md=keys.slice(-12).map(k=>{const mo=S.cashflow[k];const inc=mo.income||0;const rec=recurringIncomeTRY(mo);const exp=monthOutflowTRY(mo);const port=monthDeployment(mo).newMoney;return {k,inc,rec,exp,sur:inc-exp-port};});
  const mx=Math.max(...md.map(m=>Math.max(m.inc,m.exp)),1);
  $('cfwSurplus').innerHTML=md.length?`<div class="sbars">${md.map((m,i)=>`<div class="sbar" title="${m.k}: surplus ${fT(m.sur)}"><div class="up"><i style="height:${(m.inc/mx*46).toFixed(0)}px;animation-delay:${(i*.05).toFixed(2)}s"></i></div><div class="dn"><i style="height:${(m.exp/mx*46).toFixed(0)}px;animation-delay:${(i*.05).toFixed(2)}s"></i></div><span>${m.k.slice(5)}</span></div>`).join('')}</div>`:'<div class="neo-empty">No months yet</div>';
  const closed=md.filter(m=>m.inc>0);
  const med=a=>{if(!a.length)return 0;const s=a.slice().sort((x,y)=>x-y);return s[Math.floor(s.length/2)];};
  // Null, not 1 — the same shape reserveTargetTRY in this file already uses. S.base is USD;
  // with no rate its TRY equivalent does not exist, and the whole 12-month projection
  // below is built on it, so a fabricated rate would not be a display slip: it would
  // change the projected surplus by roughly the exchange rate. (audit 2026-08-03)
  const fx = S.fxRate > 0 ? S.fxRate : null;
  const medInc=med(closed.map(m=>m.rec)),medExp=med(closed.map(m=>m.exp)),port=fx?S.base*fx:null;
  const mSur=medInc-medExp-port; let cum=0;const pts=[];for(let i=0;i<12;i++){cum+=mSur;pts.push(cum);}
  const pmin=Math.min(0,...pts),pmax=Math.max(1,...pts),prng=(pmax-pmin)||1,W=260,Hh=70;
  const sx=i=>i/(pts.length-1)*W,sy=v=>Hh-((v-pmin)/prng)*Hh;
  let d='M0 '+sy(pts[0]).toFixed(0);pts.forEach((v,i)=>{if(i)d+=' L'+sx(i).toFixed(0)+' '+sy(v).toFixed(0);});
  $('cfwProj').innerHTML=`<svg viewBox="0 0 ${W} ${Hh}" width="100%" height="${Hh}"><defs><linearGradient id="cfpg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#00f0ff" stop-opacity=".28"/><stop offset="1" stop-color="#00f0ff" stop-opacity="0"/></linearGradient></defs><path d="${d} L${W} ${Hh} L0 ${Hh} Z" fill="url(#cfpg)" opacity="0" style="animation:cfFade 1.2s .5s forwards"/><path d="${d}" fill="none" stroke="var(--cyan)" stroke-width="2" stroke-dasharray="760" stroke-dashoffset="760" style="animation:cfDash 1.5s forwards"/></svg><div style="font-size:11px;color:var(--t3);margin-top:4px">12-mo capacity ≈ ${fT(cum)} <span style="color:var(--t2)">(${fU(fx?cum/fx:null)})</span></div>`;
  const key=now().slice(0,7),cm=S.cashflow[key];
  const proj=monthOutflowTRY(cm);
  const paid=monthOutflowTRY(cm,true);
  const burn=proj?Math.min(paid/proj*100,100):0,C=2*Math.PI*32,col=burn>=90?'var(--red)':burn>=70?'var(--yel)':'var(--grn)';
  $('cfwBurn').innerHTML=`<div style="display:flex;align-items:center;gap:14px"><svg viewBox="0 0 80 80" width="80" height="80" class="cfring"><circle cx="40" cy="40" r="32" fill="none" stroke="rgba(120,150,220,.12)" stroke-width="8"/><circle cx="40" cy="40" r="32" fill="none" stroke="${col}" stroke-width="8" stroke-linecap="round" class="cfarc" style="stroke-dasharray:${(burn/100*C).toFixed(0)} ${C.toFixed(0)}"/></svg><div><div class="mono" style="font-size:18px;font-weight:700;color:${col}">${burn.toFixed(0)}%</div><div style="font-size:10px;color:var(--t3)">${fT(paid)} / ${fT(proj)} paid</div></div></div>`;
  // "Debt-free by" is a date about the OWNER, so it is driven by the loans he pays. The
  // mortgage runs to Mar 2033 and his wife pays it; letting it set the headline turned a
  // Feb 2028 answer into a Mar 2033 one. Karma loans stay on the list — he keeps them here to
  // track them — but they are labelled and they do not move the date. (audit 2026-08-04)
  const loans=(S.expenseDefs||[]).filter(x=>x.type==='Loan'&&(x.loanRemain||0)>0)
    .map(x=>({name:x.name,rem:x.loanRemain,karma:x.paidBy===KARMA}));
  const mine=loans.filter(l=>!l.karma);
  if(loans.length){const maxR=Math.max(...loans.map(l=>l.rem));const [dfY,dfM]=cfLoanAnchor();
    const scale=mine.length?Math.max(...mine.map(l=>l.rem)):maxR;
    const df=new Date(dfY,dfM-1+scale,1);const dfLbl=df.toLocaleString('en-US',{month:'short',year:'numeric'});
    $('cfwDebt').innerHTML=loans.map((l,i)=>`<div style="margin-bottom:8px"><div style="display:flex;justify-content:space-between;font-size:11px"><span>${esc(l.name)}${l.karma?' <span style="color:var(--pur);font-size:9px">KARMA</span>':''}</span><span class="cflm">${l.rem} mo</span></div><div class="cftrack"><div class="cffill" style="width:${(100-l.rem/maxR*100).toFixed(0)}%;animation-delay:${(i*.12).toFixed(2)}s"></div></div></div>`).join('')
      +(mine.length?`<div style="font-size:11px;color:var(--grn);margin-top:6px">debt-free by <b>${dfLbl}</b><span style="color:var(--t3)"> · yours only</span></div>`
        :`<div style="font-size:11px;color:var(--pur);margin-top:6px">every remaining loan is karma \u2014 none of them is yours</div>`);
  } else $('cfwDebt').innerHTML='<div style="font-size:12px;color:var(--grn);padding:8px 0">No active loans</div>';
  const disp=medInc-medExp;
  $('cfwDisp').innerHTML=`<div class="mono" style="font-size:22px;font-weight:700;color:${disp>=0?'var(--grn)':'var(--red)'}">${fT(disp)}</div><div style="font-size:10px;color:var(--t3)">avg monthly disposable</div><div style="font-size:11px;color:var(--t2);margin-top:5px">${fU(fx?disp/fx:null)}/mo · ${fU(fx?disp/fx*12:null)}/yr</div>`;
}

function renderCF(){
  renderExpDefs();
  renderIncDefs();
  renderBudget();
  try{_cfNeo();}catch(e){console.error('[ZAGX] _cfNeo failed:',e);}
  try{_cfHeroShell();}catch(e){console.error('[ZAGX] _cfHeroShell failed:',e);}
  renderCFHist();
  renderOTIList();
  try{renderStatements();}catch(e){console.error('[ZAGX] renderStatements failed:',e);}
  // Render liabilities section (if function available via window global)
  if(window.renderLiabilities)window.renderLiabilities();
  // Top summary: aggregate across all months
  const months=Object.keys(S.cashflow);
  let totInc=0,totPort=0,totExp=0;
  let totRedep=0;
  // totPort stays NEW MONEY, because totSurplus is derived from it. Recycled capital is
  // reported beside the figure, not inside it.
  months.forEach(k=>{const mo=S.cashflow[k];const d=monthDeployment(mo);
    totInc+=mo.income||0;totPort+=d.newMoney;totRedep+=d.redeployed;
    totExp+=monthOutflowTRY(mo,true);});
  const totSurplus=totInc-totPort-totExp;
  const el=document.getElementById('cfTopSum');
  if(el)el.innerHTML=`
    <div class="neo-cell"><div class="l">Total Income (all months)</div><div class="v">${fT(totInc)}</div><div class="s">${fU(toUSDStrict(totInc, 'TRY'))}</div></div>
    <div class="neo-cell"><div class="l">Total Portfolio</div><div class="v">${fT(totPort)}</div><div class="s">${fU(toUSDStrict(totPort, 'TRY'))}${totRedep?' · +'+fT(totRedep)+' recycled':''}</div></div>
    <div class="neo-cell"><div class="l">Total Expenses</div><div class="v" style="color:var(--red)">${fT(totExp)}</div><div class="s">${fU(toUSDStrict(totExp, 'TRY'))}</div></div>
    <div class="neo-cell"><div class="l">Total Surplus</div><div class="v ${totSurplus>=0?'pos':'neg'}">${fT(totSurplus)}</div><div class="s ${totSurplus>=0?'pos':'neg'}">${fU(toUSDStrict(totSurplus, 'TRY'))}</div></div>
    <div class="neo-cell"><div class="l">Months Tracked</div><div class="v">${months.length}</div><div class="s">${months.filter(k=>S.cashflow[k].closed).length} closed</div></div>`;
  renderProjection();
  resetWhatIf();
  populateWIExpenses();
  runDebtPayoff();
}

// ===== Money-shell hero + row summaries (mockup v0.2.1). Formulas are IDENTICAL
// to renderCFHist's row math — presentation only, no new calculations. =====
function _cfHeroShell(){
  const $=id=>document.getElementById(id);
  if(!$('cfHeroNum'))return;
  const k=new Date().toISOString().slice(0,7);
  const mo=S.cashflow[k];
  const months=Object.keys(S.cashflow);
  if(!mo){
    $('cfHeroLbl').textContent='SURPLUS · NO OPEN MONTH';
    $('cfHeroNum').textContent='—';
    $('cfHeroChips').innerHTML=`<span class="mny-chip">months tracked <b>${months.length}</b></span>`;
    $('cfHeroCtx').innerHTML=`No entry for ${k} yet — <b>+ Add Month</b> to start the cycle.`;
    $('cfHeroTwin').innerHTML='';
  }else{
    // same math as the breakdown row for this month
    const paid=monthOutflowTRY(mo,true);
    void paid;
    const projected=monthOutflowTRY(mo);
    // NEW MONEY only. The recycled leg is a chip below, never a term in surplus.
    const _d=monthDeployment(mo);
    const income=mo.income||0, port=_d.newMoney;
    const surplusEst=(income-port)-projected;
    const confirmed=mo.items.filter(i=>i.confirmed).length, total=mo.items.length;
    const pending=total-confirmed;
    $('cfHeroLbl').textContent=`SURPLUS · ${k} · ESTIMATED`;
    const n=$('cfHeroNum');
    n.textContent=(surplusEst>=0?'+':'')+fT(surplusEst);
    n.style.color=surplusEst>=0?'var(--grn)':'var(--red)';
    n.style.textShadow=surplusEst>=0?'0 0 24px rgba(52,226,160,.3)':'0 0 24px rgba(255,84,112,.3)';
    const rate=income>0?Math.round(surplusEst/income*100):0;
    $('cfHeroChips').innerHTML=
      `<span class="mny-chip">savings rate <b>${income>0?rate+'%':'—'}</b></span>`+
      `<span class="mny-chip">≈ <b>${fU(toUSDStrict(surplusEst, 'TRY'))}</b></span>`+
      `<span class="mny-chip">recon <b>${confirmed}/${total}</b></span>`+
      (_d.redeployed?`<span class="mny-chip">recycled <b>${fT(_d.redeployed)}</b></span>`:'');
    $('cfHeroCtx').innerHTML=
      `Income covers projected expenses${port>0?' and the portfolio commitment':''}${surplusEst>=0?` with <b>${fT(surplusEst)}</b> to spare`:` — <b>${fT(Math.abs(surplusEst))} short</b>`}. `+
      (pending>0?`<b>${pending} payment${pending===1?'':'s'} still pending</b> this month.`:(mo.closed?'Month is closed.':'All payments reconciled.'));
    const expTot=port+projected;
    const mx=Math.max(income,expTot,1);
    $('cfHeroTwin').innerHTML=
      `<div class="tl">INCOME ${fT(income)}</div><div class="mny-tbar inc"><i style="width:${Math.round(income/mx*100)}%"></i><span>${Math.round(income/mx*100)}%</span></div>`+
      `<div class="tl">EXPENSE ${fT(projected)}${port>0?' · PORTFOLIO '+fT(port):''}</div><div class="mny-tbar exp"><i style="width:${Math.round(expTot/mx*100)}%"></i><span>${Math.round(expTot/mx*100)}%</span></div>`;
  }
  // header meta + ledger row summaries
  const closed=months.filter(x=>S.cashflow[x].closed).length;
  const meta=$('cfMeta');
  if(meta)meta.innerHTML=`<b>${months.length}</b> months tracked · ${closed} closed`;
  const hs=$('cfHistSum');
  if(hs){
    const open=months.filter(x=>!S.cashflow[x].closed).sort().reverse()[0];
    hs.innerHTML=open?`<span class="gold">${open} OPEN</span> · ${months.length} months`:`${months.length} months · all closed`;
  }
  const es=$('cfExpSum');
  if(es){
    const defs=(S.expenseDefs||[]).filter(d=>d.active!==false);
    es.textContent=`${defs.length} definition${defs.length===1?'':'s'}`;
  }
  const os=$('cfOtiSum');
  if(os){
    const oti=S.oneTimeIncome||[];
    const tot=oti.reduce((s,e)=>s+(e.currency==='TRY'?(e.amount||0):0),0);
    os.innerHTML=oti.length?`<span class="pos">${oti.length} entr${oti.length===1?'y':'ies'}</span>${tot?` · ${fT(tot)} TRY`:''}`:'none yet';
  }
}

let chProj=null;
function renderProjection(){
  const today=new Date();
  const projMonths=[];
  for(let i=0;i<12;i++){
    const d=new Date(today.getFullYear(),today.getMonth()+i);
    const y=d.getFullYear(),m=d.getMonth()+1;
    const k=y+'-'+String(m).padStart(2,'0');
    projMonths.push({k,y,m});
  }

  // Projected income = trailing 3-month median of closed months (Salary entries, bonus excluded)
  const projIncome=projectMonthlyIncome(3);

  // Portfolio commitment (standard monthly)
  // S.base is USD; this is its TRY equivalent. `|| 1` printed the dollar figure as lira.
  const _pc=usdToTRY(S.base); const portComm=_pc==null?null:Math.round(_pc);

  // Build projection rows
  let cumSurplus=0;
  const _now=new Date();const _nowY=_now.getFullYear(),_nowM=_now.getMonth()+1;
  const rows=projMonths.map(({k,y,m})=>{
    // Check if we have actual data for this month
    const actual=S.cashflow[k];
    const isClosed=actual&&actual.closed;
    const hasConfirmedExp=actual&&actual.items&&actual.items.some(it=>it.confirmed&&it.amount>0);

    // Income: use actual if closed, otherwise trailing-median projection
    const income=isClosed?(actual.income||0):Math.round(projIncome);

    // Expenses: def-based projection for future months
    let expenses=0;
    const activeLoans=[];
    const events=[];
    (S.expenseDefs||[]).forEach(d=>{
      // DUE as well as active: this chart walks real calendar months, so an annual fee belongs
      // in the month it bills and in no other (whatif.js got the same gate in 45157f3; this loop
      // was missed there and showed the school fee in all twelve months).
      const active=defActiveForMonth(d,y,m)&&defDueForMonth(d,y,m);
      if(active){
        // Fixed defs: use fixedAmount. Variable defs: use trailing median of actual confirmed amounts
        const amt=d.fixed?d.fixedAmount:(d.fixedAmount||_projectExpenseForDef(d.id,3));
        expenses+=amt;
        if(d.type==='Loan')activeLoans.push(d.name);

        // Check if this loan ends this month
        if(d.type==='Loan'){
          let isLastMonth=false;
          if(d.loanRemain>0){
            const [_ay,_am]=cfLoanAnchor();
            const mAhead=(y-_ay)*12+(m-_am);
            if(mAhead===d.loanRemain-1)isLastMonth=true;
          }else if(d.loanStart&&d.loanTotal&&/^\d{4}-\d{2}$/.test(d.loanStart)){
            const sp=d.loanStart.split('-').map(Number);
            const elapsed=(y-sp[0])*12+(m-sp[1]);
            if(elapsed===d.loanTotal-1)isLastMonth=true;
          }
          if(isLastMonth)events.push(d.name+' ends');
        }
      }
    });

    // If actual confirmed expense data exists (closed or open), use it instead
    if(hasConfirmedExp){
      expenses=monthOutflowTRY(actual,true);
    }

    const portfolio=isClosed?monthDeployment(actual).newMoney:portComm;
    const surplus=income-expenses-portfolio;
    cumSurplus+=surplus;

    return{k,income,expenses,portfolio,surplus,cumSurplus,activeLoans:activeLoans.length,loanNames:activeLoans,events,hasActual:isClosed||hasConfirmedExp};
  });

  // Summary stats
  const totalInc12=rows.reduce((s,r)=>s+r.income,0);
  const totalExp12=rows.reduce((s,r)=>s+r.expenses,0);
  const totalPort12=rows.reduce((s,r)=>s+r.portfolio,0);
  const totalSurplus12=totalInc12-totalExp12-totalPort12;
  const avgSurplus=totalSurplus12/12;
  const _loansEndingSoon=rows.filter(r=>r.events.length>0);
  // Investment capacity: surplus after expenses, before portfolio
  const investCapacity=totalInc12-totalExp12;

  const sumEl=document.getElementById('projSum');
  if(sumEl)sumEl.innerHTML=`
    <div class="neo-cell"><div class="l">12mo Income</div><div class="v">${fT(totalInc12)}</div><div class="s">${fU(toUSDStrict(totalInc12, 'TRY'))}</div></div>
    <div class="neo-cell"><div class="l">12mo Expenses</div><div class="v" style="color:var(--red)">${fT(totalExp12)}</div><div class="s">${fU(toUSDStrict(totalExp12, 'TRY'))}</div></div>
    <div class="neo-cell"><div class="l">12mo Portfolio</div><div class="v">${fT(totalPort12)}</div><div class="s">${fU(toUSDStrict(totalPort12, 'TRY'))}</div></div>
    <div class="neo-cell"><div class="l">12mo Surplus</div><div class="v ${totalSurplus12>=0?'pos':'neg'}">${fT(totalSurplus12)}</div></div>
    <div class="neo-cell"><div class="l">Avg Monthly</div><div class="v ${avgSurplus>=0?'pos':'neg'}">${fT(avgSurplus)}</div></div>
    <div class="neo-cell"><div class="l">Invest Capacity</div><div class="v">${fT(investCapacity)}</div><div class="s">${fU(toUSDStrict(investCapacity, 'TRY'))} / year</div></div>`;

  // Chart
  const labels=rows.map(r=>r.k.slice(2));// YY-MM
  chProj=_safeDestroy(chProj);
  chProj=_safeChart('chProj',{type:'bar',
    data:{labels,datasets:[
      {label:'Income',data:rows.map(r=>r.income),backgroundColor:'rgba(110,238,180,0.6)',borderRadius:3,order:2},
      {label:'Expenses',data:rows.map(r=>-r.expenses),backgroundColor:'rgba(255,107,138,0.5)',borderRadius:3,order:2},
      {label:'Portfolio',data:rows.map(r=>-r.portfolio),backgroundColor:'rgba(123,63,228,0.4)',borderRadius:3,order:2},
      {label:'Cumulative',data:rows.map(r=>r.cumSurplus),type:'line',borderColor:'#D4A853',backgroundColor:'transparent',tension:.3,pointRadius:3,borderWidth:2,order:1}
    ]},
    options:{responsive:true,maintainAspectRatio:false,interaction:{mode:'index',intersect:false},
      plugins:{legend:{position:'bottom',labels:{color:getChartTheme().text,font:{size:10}}},
        tooltip:{backgroundColor:getChartTheme().tooltipBg,titleColor:getChartTheme().tooltipText,bodyColor:getChartTheme().text,callbacks:{label:ctx=>{const v=ctx.parsed.y;return ctx.dataset.label+': ₺'+Math.abs(v).toLocaleString('tr-TR',{minimumFractionDigits:0});}}}},
      scales:{x:{ticks:{color:getChartTheme().textMuted,font:{size:10}},grid:{display:false},stacked:true},
        y:{ticks:{color:getChartTheme().textMuted,font:{size:10},callback:v=>'₺'+v.toLocaleString()},grid:{color:getChartTheme().grid},stacked:true}}}
  });

  // Table
  const bodyEl=document.getElementById('projBody');
  if(bodyEl)bodyEl.innerHTML=rows.map(r=>{
    const rowStyle=r.hasActual?'opacity:0.7':'';
    const tag=r.hasActual?'<span style="font-size:9px;color:var(--grn)">actual</span>':'';
    return`<tr style="${rowStyle}">
      <td><strong>${r.k}</strong> ${tag}</td>
      <td class="tr">${fT(r.income)}</td>
      <td class="tr" style="color:var(--red)">${fT(r.expenses)}</td>
      <td class="tr" style="color:var(--pur)">${fT(r.portfolio)}</td>
      <td class="tr ${r.surplus>=0?'pos':'neg'}">${fT(r.surplus)}</td>
      <td class="tr ${r.cumSurplus>=0?'pos':'neg'}" style="font-weight:600">${fT(r.cumSurplus)}</td>
      <td style="font-size:11px">${r.activeLoans>0?r.activeLoans+' ('+r.loanNames.join(', ')+')':'—'}</td>
      <td style="font-size:11px;color:var(--grn)">${r.events.length?r.events.join(', '):'—'}</td>
    </tr>`;
  }).join('');
}

function renderExpDefs(){
  const el=document.getElementById('expDefList');if(!el)return;
  if(!S.expenseDefs.length){el.innerHTML='<div class="neo-empty">No definitions yet. Click + Add.</div>';return;}
  el.innerHTML=S.expenseDefs.map(d=>{
    const typeBadge=d.type==='Loan'?'<span style="color:var(--yel)">Loan</span>':'<span style="color:var(--pur)">Card</span>';
    const amtStr=d.fixed?fT(d.fixedAmount):'Variable';
    const rateInfo=d.type==='Loan'&&d.interestRate?` · <span style="color:var(--t2)">${d.interestRate}%</span>`:'';
    const loanInfo=d.type==='Loan'&&d.loanRemain?` · <span style="color:var(--ora)">${d.loanRemain} left</span>${rateInfo}`:'';
    const inactiveBadge=d.active===false?' · <span style="color:var(--red);font-size:10px;letter-spacing:.5px">INACTIVE</span>':'';
    return`<div style="padding:8px;border-bottom:1px solid var(--bd);cursor:pointer;display:flex;justify-content:space-between;align-items:center${d.active===false?';opacity:.55':''}" data-action="openExpDef" data-args='["${d.id}"]'>
      <div><strong>${esc(d.name)}</strong> <span style="color:var(--t3)">Day ${d.day}</span><br>
      <span style="font-size:11px">${typeBadge} · ${amtStr}${loanInfo}${inactiveBadge}</span></div>
      <span style="color:var(--t3);font-size:16px">›</span></div>`;
  }).join('');
}

function renderCFHist(){
  const el=document.getElementById('cfHistBody');if(!el)return;
  const months=Object.keys(S.cashflow).sort().reverse();
  if(!months.length){el.innerHTML='<tr><td colspan="9" class="neo-empty">No months yet. Click + Add Month to start.</td></tr>';return;}
  el.innerHTML=months.map(k=>{
    const mo=S.cashflow[k];
    const paid=monthOutflowTRY(mo,true);
    const projected=monthOutflowTRY(mo);
    const _d=monthDeployment(mo);
    // Disposable subtracts NEW MONEY only. _d.redeployed is displayed beside it and enters
    // nothing — see monthDeployment's docblock.
    const disposable=(mo.income||0)-_d.newMoney;
    const surplusEst=disposable-projected;
    const confirmed=mo.items.filter(i=>i.confirmed).length;
    const total=mo.items.length;
    const pct=total>0?Math.round((confirmed/total)*100):0;
    const statusBadge=mo.closed?'<span style="color:var(--grn)">✓ Closed</span>':'<span style="color:var(--yel)">Open</span>';
    return`<tr>
      <td><strong>${k}</strong></td>
      <td class="tr">${fT(mo.income||0)}</td>
      <td class="tr">${fT(_d.newMoney)}${_d.redeployed?`<div style="font-size:10px;color:var(--t2)">+${fT(_d.redeployed)} recycled</div>`:''}</td>
      <td class="tr" style="color:var(--red)">${fT(paid)}</td>
      <td class="tr" style="color:var(--ora)">${fT(projected)}</td>
      <td class="tr ${surplusEst>=0?'pos':'neg'}">${fT(surplusEst)}</td>
      <td>${confirmed}/${total} (${pct}%)</td>
      <td>${statusBadge}</td>
      <td><button class="btn btn-s" style="font-size:10px" data-action="openCFMonth" data-args='["${k}"]'>Open</button> <button class="btn btn-s btn-d" style="font-size:10px" data-action="cfDeleteMonth" data-args='["${k}"]'>Del</button></td></tr>`;
  }).join('');
}

// ===== ONE-TIME INCOME =====

/**
 * Did this one-time-income record actually move the balance?
 *
 * saveOTI credits CONDITIONALLY: a receipt dated on or before the last bank reading is
 * already inside that reading, so it is written as an `_audit` row and S.cash is left alone.
 * deleteOTI reversed unconditionally, so deleting such a record subtracted money that had
 * never been added — silently, because an `_audit` row is invisible to the ledger sum that
 * would otherwise have caught it. The row records what happened; ask the row.
 *
 * Exported so the asymmetry is pinned by a test rather than by a comment. (audit 2026-08-10)
 */
export function _otiWasCredited(cashTxs, otiId) {
  const rows = (Array.isArray(cashTxs) ? cashTxs : []).filter(tx => tx && tx._otiId === otiId);
  // NO ROW AT ALL IS NOT "IT NEVER CREDITED". cashTxs is capped at 2,000 and the oldest are
  // dropped, and a cloud merge can carry the record without its row. Answering "false" there
  // would leave the balance permanently high on every pre-cap receipt — the mirror image of
  // the bug this function exists to fix. Only a mirror row is evidence of no credit; an
  // absent row means ask the anchor instead, so this returns null for "cannot tell".
  if (!rows.length) return null;
  return rows.some(tx => !tx._audit);
}

// Set by openOTIFor() when another module (today: the tax page) opens this modal with a
// known origin. Written onto the saved entry so the link between "the engine says this
// refund was paid" and "this money is in the ledger" is a FIELD, not a note the matcher has
// to parse. Cleared on every open so a stale origin cannot leak onto the next manual entry.
let _otiOrigin = null;

function openOTI() {
  _otiOrigin = null;
  document.getElementById('otiType').value = 'Bonus';
  document.getElementById('otiDate').value = new Date().toISOString().slice(0, 10);
  document.getElementById('otiAmount').value = '';
  document.getElementById('otiCur').value = 'TRY';
  document.getElementById('otiNote').value = '';
  document.getElementById('moOTI').classList.add('active');
}

/**
 * Open the one-time-income modal pre-filled from another module.
 *
 * DELIBERATELY NOT A SILENT WRITE. The tax engine knows the refund landed, but only the
 * owner knows it landed in an account this app tracks, on the date the letter says, and was
 * not already booked by hand. So the bridge fills the form and he presses Save — the same
 * door, the same anchor guard, the same audit row as any other receipt.
 *
 * @param {{type?:string,date?:string,amount?:number,currency?:string,note?:string,origin?:Object}} pre
 */
export function openOTIFor(pre) {
  const p = pre || {};
  openOTI();
  _otiOrigin = p.origin || null;
  if (p.type) document.getElementById('otiType').value = p.type;
  if (p.date) document.getElementById('otiDate').value = p.date;
  if (p.amount != null) document.getElementById('otiAmount').value = String(p.amount);
  if (p.currency) document.getElementById('otiCur').value = p.currency;
  if (p.note) document.getElementById('otiNote').value = p.note;
}

function saveOTI() {
  const type = document.getElementById('otiType').value;
  const date = document.getElementById('otiDate').value;
  const amount = parseFloat(document.getElementById('otiAmount').value);
  const currency = document.getElementById('otiCur').value;
  const note = document.getElementById('otiNote').value.trim();
  if (!date || !amount || amount <= 0) return toast('Enter date and amount', 'a-r');
  if (!S.oneTimeIncome) S.oneTimeIncome = [];
  const entry = { id: uid(), date, type, amount, currency, note };
  if (_otiOrigin && _otiOrigin.taxYear != null) {
    entry.taxYear = Number(_otiOrigin.taxYear);
    if (_otiOrigin.taxRef) entry.taxRef = String(_otiOrigin.taxRef);
  }
  _otiOrigin = null;
  stampItem(entry);
  S.oneTimeIncome.push(entry);
  // Real deposit — bonus/commission etc. is actual money received
  // Only if it landed AFTER the last bank reading. A bonus that hit Garanti on the 3rd is
  // already inside the figure he typed on the 8th; crediting it again on the 10th put the app
  // ₺50,000 above the banks, with a non-audit row so nothing could see it. Recording the same
  // bonus as a cash-flow income row was already handled this way — the two doors have to agree.
  const _otiAfterAnchor = (() => {
    const a = cashAnchorDay();
    if (!a) return true; // never reconciled: the old running-total behaviour is still correct
    const d = String(date || '').slice(0, 10);
    return d > a && d <= todayLocal();
  })();
  if (_otiAfterAnchor) S.cash[currency] = (S.cash[currency] || 0) + amount;
  const txNote = 'one-time: ' + type + (note ? ' — ' + note : '');
  S.cashTxs.unshift({
    id: uid(), date: date + 'T12:00:00', type: 'deposit',
    amount, currency, note: txNote,
    // A row that did not move the balance must not count in the ledger either, or reconcile.js
    // "repairs" the balance back up on the next boot and the double-count returns by another door.
    _otiId: entry.id, _audit: !_otiAfterAnchor, _updatedAt: Date.now()
  });
  save();
  // The tax page decides whether to offer "Record in cash" by asking whether a booking
  // exists. Without this, that page keeps painting the pre-save answer — an amber
  // NOT IN CASH band with a live button — and a second click credits the money twice.
  markDirty('tax', 'integrity', 'dash');
  clMo('moOTI');
  renderOTIList();
  renderCF();
  lg('income', type, f(amount, 2) + ' ' + currency + (note ? ' — ' + note : ''));
  toast(type + ' recorded', 'a-g');
}

async function deleteOTI(id) {
  if (!await uiConfirm('Delete this one-time income record?', { title: 'Delete income', okText: 'Delete', danger: true })) return;
  const idx = (S.oneTimeIncome || []).findIndex(e => e.id === id);
  if (idx < 0) return toast('Not found', 'a-r');
  const entry = S.oneTimeIncome[idx];
  // Reverse the cash deposit ONLY IF ONE WAS MADE.
  // saveOTI credits conditionally — a receipt dated before the last bank reading is already
  // inside that reading, so it is written as an `_audit` row and the balance is left alone.
  // Reversing unconditionally therefore subtracted money that had never been added, and did
  // it silently: the ledger and the balance disagreed with nothing on screen to say so.
  // The row itself carries the answer, so ask the row. (audit 2026-08-10)
  const _known = _otiWasCredited(S.cashTxs, id);
  // Fall back to the rule saveOTI applied when the evidence is gone: it credits only a
  // receipt that post-dates the last bank reading.
  // BOTH halves of saveOTI's rule, not just the lower one. A receipt dated in the FUTURE is
  // not credited either (`d <= todayLocal()`), and dropping that half made the reversal fire
  // on a credit that had never happened — the same asymmetry, on the fallback branch.
  const _credited = _known == null
    ? (() => {
      const a = cashAnchorDay();
      const d = String(entry.date || '').slice(0, 10);
      return !a || (d > a && d <= todayLocal());
    })()
    : _known;
  if (_credited) S.cash[entry.currency] = (S.cash[entry.currency] || 0) - entry.amount;
  // Tombstone the OTI record + its cash tx(s) so a merge can't resurrect them (would re-deposit the reversed cash)
  (S.cashTxs || []).forEach(tx => { if (tx._otiId === id) tombstoneId(tx.id); });
  S.cashTxs = S.cashTxs.filter(tx => tx._otiId !== id);
  softDelete('oneTimeIncome', id);
  save();
  markDirty('tax', 'integrity', 'dash');
  renderOTIList();
  renderCF();
  toast(entry.type + ' deleted', 'a-b');
}

function renderOTIList() {
  const el = document.getElementById('otiList');
  if (!el) return;
  const items = [...(S.oneTimeIncome || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  if (!items.length) {
    el.innerHTML = '<div class="neo-empty">No one-time income recorded yet. Use + Add to log bonuses, commissions, etc.</div>';
    return;
  }
  el.innerHTML = items.map(e => {
    const sym = e.currency === 'USD' ? '$' : e.currency === 'EUR' ? '€' : '₺';
    return `<div style="display:flex;justify-content:space-between;align-items:center;padding:5px 0;border-bottom:1px solid var(--bd)">
      <div>
        <span class="b-open" style="font-size:9px;margin-right:6px">${esc(e.type)}</span>
        <span style="color:var(--t2);font-size:11px">${e.date}</span>
        ${e.note ? `<span style="color:var(--t3);font-size:10px;margin-left:6px">${esc(e.note)}</span>` : ''}
      </div>
      <div style="display:flex;align-items:center;gap:8px">
        <span style="color:var(--grn);font-weight:600">${sym}${f(e.amount, 2)}</span>
        <button class="btn btn-s btn-d" data-action="deleteOTI" data-args='["${e.id}"]' style="font-size:9px;padding:1px 4px">×</button>
      </div>
    </div>`;
  }).join('');
}

function _median(arr) {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * Forward essential monthly outflow (TRY), excl-karma. Sums recurring defs that
 * are non-mixed AND still active next month (rolled-off loans drop out via
 * defActiveForMonth) plus the trailing median of non-def, non-mixed confirmed
 * living items from closed months. Basis for the derived reserve target.
 *
 * ⚠ ONE-OFFS ARE EXCLUDED, AND BEFORE 2026-10-03 THERE WAS NO WAY TO SAY SO.
 *
 * This function answers "what leaves every month from here on", and it is multiplied by 6 for the
 * reserve target and by 12 for the FI number. Every confirmed non-def item in a closed month
 * entered it, with no concept of a payment that happens once. Measured on the live book:
 *
 *     2026-08  USA Visa                 TRY 21,300   paid once, ever
 *     2026-09  School Transportation-1  TRY 75,000   installment 1 of 2
 *
 * Those two WERE the whole trailing median: TRY 48,150 a month, claiming TRY 288,900 of reserve
 * cover for a visa fee that will not recur and a two-installment school fee. When September closed,
 * that median went 21,300 -> 48,150 and the card projections rolled their window, and the reserve
 * target went $21,790 -> $29,709 against coverage of $22,662 that had not moved by a cent. The
 * owner opened Goals and found himself demoted from Debt-free to Survival having done nothing.
 *
 * `oneOff: true` on an item (or on a definition, which `cfGenItems` passes down) keeps it in the
 * month's cash plan, in `monthOutflowTRY`, in `cashflow.incomeOverflow` and in every surplus
 * figure — it is real money and it really left — while keeping it out of the FORWARD basis. The
 * question this function asks is the only one the flag changes.
 *
 * NOT SOLVED HERE, and worth stating: a school term fee paid in two installments is not TRY 0 a
 * month either. The honest figure is the annual total amortised, and this app has no amortisation
 * for expenses. Excluding it understates the target by the amount of that fee; including it
 * overstated it by 6x. The flag picks the smaller error and names the larger one.
 */
function forwardEssentialMonthlyTRY() { // in the export block below; Alpha Ledger FI-days uses it (SSOT)
  const n = new Date();
  const nY = n.getMonth() === 11 ? n.getFullYear() + 1 : n.getFullYear();
  const nM = n.getMonth() === 11 ? 1 : n.getMonth() + 2; // next month, 1-based
  let defPart = 0;
  (S.expenseDefs || []).forEach(d => {
    if (d.paidBy === KARMA) return;             // karma default → someone else pays
    const every = recurEveryOf(d);
    if (every === 0) return;                    // declared non-recurring — see the note below
    // ⚠ ACTIVE, NOT DUE. `defActiveForMonth` asks "does this definition still exist next month";
    // `defDueForMonth` asks "does it bill THEN". The basis is a monthly AVERAGE, so an annual fee
    // belongs in it every month at a twelfth, not in one month at twelve twelfths. Using the due
    // test here would make the reserve target lurch by the whole fee in the month it falls.
    if (!defActiveForMonth(d, nY, nM)) return;  // ended loans drop out
    const amt = d.fixed ? (d.fixedAmount || 0) : (d.fixedAmount || _projectExpenseForDef(d.id, 3) || 0);
    defPart += amt / every;
  });
  // The trailing median covers items that have NO definition. An item whose name now has one
  // is counted above, in defPart — counting it here as well would bill the same card twice.
  // Before v44 no card had a definition, so this set was empty and the behaviour is unchanged
  // for anything that is still typed ad hoc.
  const defNames = new Set((S.expenseDefs || []).map(_cfName).filter(Boolean));
  const sums = [];
  Object.values(S.cashflow || {}).forEach(mo => {
    if (!(mo && (mo.closed || mo.status === 'closed'))) return;
    const s = (mo.items || []).reduce((a, it) =>
      a + ((!it.defId && !defNames.has(_cfName(it)) && it.confirmed && it.amount && !isKarma(it))
        ? (recurEveryOf(it) === 0 ? 0 : it.amount / recurEveryOf(it)) : 0), 0);
    if (s > 0) sums.push(s);
  });
  return defPart + _median(sums);
}

/**
 * Derived reserve target (USD) = 6 x forward essential monthly outflow (excl-karma).
 * A manual override wins only when explicitly set (reserveTargetManual + a positive
 * reserveTargetUSD). Falls back to any stored target, then a documented floor.
 */
function reserveTargetUSD() {
  const st = S.settings || {};
  if (st.reserveTargetManual && Number(st.reserveTargetUSD) > 0) return Number(st.reserveTargetUSD);
  // `|| 1` derived the target at 1 TRY = 1 USD, inflating it ~40x. That target is the
  // DENOMINATOR of every reserve percentage in the app and the basis of the Bonus
  // planner's reserve gap. Without a rate, fall through to the stored/floor value
  // instead of publishing a fabricated one. (audit 2026-08-01)
  const fx = S.fxRate > 0 ? S.fxRate : null;
  const derived = fx ? forwardEssentialMonthlyTRY() * 6 / fx : 0;
  if (derived > 0) return derived;
  return Number(st.reserveTargetUSD) > 0 ? Number(st.reserveTargetUSD) : 12000;
}

/** Strategic Reserve (USD): open daily/time-deposit PRINCIPAL only (yield is earnings, not reserve). */
function strategicReserveUSD() {
  return (S.trades || [])
    .filter(t => t.type === 'TimeDeposit' && t.td && t.status !== 'Closed')
    .reduce((s, t) => s + toUSD(t.td.principal || 0, t.currency || 'TRY'), 0);
}

// Exports
export {
  parseRecurEvery,
  isKarma,
  monthOutflowTRY,
  monthKarmaTRY,
  isRecurringIncome,
  recurringIncomeTRY,
  oneOffIncomeTRY,
  forwardEssentialMonthlyTRY,
  reserveTargetUSD,
  strategicReserveUSD,
  cfGetMonth,
  cfRecalcIncome,
  backfillSalaryCashTxs,
  openExpDef,
  toggleEdLoan,
  saveExpDef,
  deleteExpDef,
  renderIncDefs,
  projectMonthlyIncome,
  _projectExpenseForDef,
  defActiveForMonth,
  recurEveryOf,
  defDueForMonth,
  cfGenItems,
  openCFMonth,
  cfMoChanged,
  cfAddIncome,
  cfRemoveIncome,
  cfSetIncome,
  cfIncomeFooter,
  renderCFIncome,
  renderCFModal,
  cfCalcSummary,
  cfAddItem,
  cfRemoveItem,
  cfSetAmt,
  cfSetField,
  cfPaidByChanged,
  cfOneOffChanged,
  cfToggle,
  saveCFMo,
  closeCFMonth,
  cfDeleteMonth,
  renderCF,
  renderProjection,
  renderExpDefs,
  renderCFHist,
  openOTI,
  saveOTI,
  deleteOTI,
  renderOTIList
};
